// Shared "Outlook" component — the whole-environment health view used by BOTH
// the dashboard SPA and the embeddable widget. Presentational only: each host
// supplies the data (its own confidence hook) + the nav callbacks, and loads the
// matching styles (`@netwatchai/chat-core/outlook.css`; the widget inlines it
// into its shadow DOM). Ported from the dashboard OutlookPage design + the
// widget's day drill-down.

import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react'
import { NwLineChart, type NwSeries } from './NwLineChart'
import { EmptyState } from './EmptyState'
import { SeverityIcon } from './Severity'
import { SEVERITY, type SeverityKey } from './severity'
import type { Chart as ChartJSInstance } from 'chart.js'
import { dayModalCounts, dayTrendSeries, type HourPoint } from './daySummary'
import { ChartActions } from './ChartActions'
import { ChartDialog } from './ChartDialog'
import { NwChart } from './NwChart'
import type { ChartSpec } from './chartSpec'
import { useOutsidePress } from './useOutsidePress'

// --- canonical data shapes (both hosts map their hook output to these) -------

export interface ConfidenceData {
  score: number
  hosts: number
  affected_hosts: number
  /** severity (0..5, JSON string keys) → hosts whose WORST active problem is
   *  that severity; values sum to affected_hosts. null/absent on a degraded
   *  reading or a pre-upgrade cached payload — the tile then falls back to the
   *  flat count (no severity split). */
  affected_by_severity?: Record<string, number> | null
  active_incidents: number
  /** AI one-liner explaining the whole environment (the "AI Insight" banner).
   *  null on a pre-warm/degraded payload — the UI falls back to a count sentence. */
  insight?: string | null
  today: { issues_opened: number; issues_resolved: number }
}

export interface ConfidenceDay {
  day: string
  score: number
  issues_opened: number
  issues_resolved: number
  /** Worst severity (0..5) among THAT DAY'S problems still unresolved at the
   *  end-of-day rollup (carried-over problems belong to the overdue pill, not
   *  the day cell). -1 = the day's problems all resolved (green check).
   *  null/absent = row compiled before this field existed — no truthful
   *  reading, so the strip renders that day as an empty cell. */
  worst_severity?: number | null
  /** Problems still active at close; the strip's day total is
   *  issues_resolved + this. null/absent = pre-upgrade row. */
  active_at_close?: number | null
  /** Problems opened that day and still unresolved at close — the strip's
   *  per-day count. null/absent = pre-upgrade row (empty cell). */
  new_unresolved?: number | null
}

export interface ActiveProblem {
  host: string
  name: string
  severity: number
  since: number
  eventid?: string | null
}

/** One event anchor referenced by a {{tN}} token in insight text. Rendered
 *  as absolute time (client-env TZ) + a viewer-relative hint: "02:05 (6h ago)". */
export interface InsightAnchor {
  epoch: number
  label?: string
}

/** One computed environment insight (GET /confidence/insights), rank order.
 *  The tile rotates the top of the list; the drawer shows every line. */
export interface EnvironmentInsight {
  /** Stable identity across sweeps — dismissals and NEW badges key on it. */
  identity?: string
  kind: string       // pattern | correlation | risk | hygiene | posture
  headline: string   // may contain {{tN}} anchor tokens
  detail: string
  /** Slow-lane LLM reading on multi-event security rows — preferred over
   *  `detail` when present; may contain {{tN}} tokens. */
  enrichment?: string | null
  /** v3: hedged read-only recommendation ("consider…"); absent on legacy
   *  rows until their next sweep — hidden when missing. */
  recommended_action?: string | null
  ask: string        // chat seed prompt for the popup's Ask button
  /** The computed join backing this line — rendered in the popup so the
   *  fact is verifiable in place. */
  evidence?: Record<string, unknown> | null
  anchors?: InsightAnchor[] | null
  tier?: string      // factual | temporal | mechanism
  lane?: string      // slow (6h sweep) | fast (security lane)
  severity?: number
  first_seen?: string | null   // NEW badge while < 24h old
  last_seen?: string | null
  computed_at?: string | null  // freshness "i" icon anchor
  /** Client-environment IANA timezone absolute anchor times render in. */
  tz?: string | null
  /** Server-derived display-family key — the drawer groups on it;
   *  "correlated" is pinned first. */
  family?: string | null
  /** all | admin — any admin row visible ⇒ this session may refresh lanes. */
  visibility?: string | null
}

/** THIS user's live rating of one insight (GET /confidence/insights/ratings).
 *  Rating is signal for the learning pipe, never a visibility action —
 *  a rated line renders exactly like an unrated one. */
export interface InsightRatingState {
  thumb: 'up' | 'down'
  /** Set on 👎 only: not_useful | expected_behavior | too_noisy | incorrect. */
  reason?: string
}

export interface DayDetail {
  score: number
  issues_opened: number
  issues_resolved: number
  active_incidents: number
  severity_breakdown: Record<number, number>
  trend: { at?: string; score: number }[]
  /** Hourly health/opened/resolved points for the trend chart. Absent on a
   *  pre-upgrade backend — the chart falls back to the score-only trend. */
  hourly?: HourPoint[]
}

/** One compiled past day (GET /confidence/day/{day}) for the day drill-down.
 *  null fields = the row predates the end-of-day state columns, or — for
 *  severity_breakdown — Zabbix was unreachable for the one-time
 *  reconstruction (retried on the next open). */
export interface DayHistoryDetail {
  day: string
  score: number
  issues_opened: number
  issues_resolved: number
  /** Problems still active at close (modal day total = issues_resolved + this). */
  active_at_close: number | null
  /** Problems opened that day and still unresolved at close. */
  new_unresolved: number | null
  /** Carried-over problems (opened earlier) still open at close. */
  overdue_at_close: number | null
  /** severity (0..5) -> active problem count at close. */
  severity_breakdown: Record<number, number> | null
  trend: { at?: string; score: number }[]
  /** Hourly health/opened/resolved points for the trend chart. */
  hourly?: HourPoint[]
}

/** One filesystem on a path to fill (from `/confidence/disks`), soonest-ETA
 *  first. `pct` is null for Windows free-bytes drives (no total exposed). */
export interface FillingDisk {
  host: string
  drive: string
  pct: number | null
  total_gb?: number | null
  free_gb?: number | null
  days_to_full?: number | null
  days_to_threshold?: number | null
  state: string
  confidence?: number | null
  recent_jump?: boolean
}

/** One point on a disk trend chart (Unix epoch seconds → value on the shared
 *  axis: percent, or GB for Windows free-space drives). */
export interface DiskTrendPoint {
  t: number
  y: number
}

/** Response of `GET /confidence/disks/trend` — real history (solid) + the
 *  projected fill-up (dotted) on one shared axis. `available` is false when
 *  there's no real history to plot (monitoring hiccup / unresolved item), in
 *  which case only the forecast line is meaningful. */
export interface DiskTrendData {
  unit: string                 // "%" | "GB"
  threshold: number | null     // warning line (90 for percent), null for GB
  history: DiskTrendPoint[]
  forecast: DiskTrendPoint[]
  current: number
  available: boolean
}

// --- "Ask why" investigation shapes -----------------------------------------
// Mirror the backend RootCause / CorrelationReport that the investigate_alert
// flow persists on each incident (snake_case = the wire shape, like the types
// above). All fields defensive: a report can be partial.

export interface PeerHit { host: string; hostid?: string; eventid?: string | null }
export interface HostProblem { name: string; severity: number; eventid?: string | null }
export interface Claim {
  claim: string
  evidence: string
  /** Deterministic-check id backing this claim (R4), when one applies. */
  check?: string | null
}

export interface CaseReport {
  statement: string
  category?: string
  causal_chain?: string[]
  validated_claims?: Claim[]
  non_validated_claims?: string[]
  validity_score?: number
  remediation_steps?: string[]
  /** Code-derived three-way verdict (R4). Absent on pre-R4 reports. */
  verdict_state?: 'confirmed' | 'probable' | 'inconclusive' | null
  /** Hypotheses the deterministic checks refuted. */
  ruled_out?: string[]
  /** Check ids the diagnosis relied on. */
  checks_cited?: string[]
  /** Inconclusive verdicts only: what would settle it (replaces remediation). */
  next_checks?: string[]
}

export interface Correlation {
  peers: PeerHit[]
  host_other_problems: HostProblem[]
  recurrence_24h: number
  recurrence_7d: number
}

export interface OutlookProps {
  /** The live environment score (null while loading / on error). */
  confidence: ConfidenceData | null
  history: ConfidenceDay[]
  problems: ActiveProblem[]
  /** Top filling disks for the "Capacity trend" tile. null = still loading,
   *  [] = nothing filling (calm state). Soonest-ETA first. */
  disks?: FillingDisk[] | null
  /** Mined AI environment insights for the rotating "AI Insight" tile.
   *  null / [] = fall back to the posture one-liner (`confidence.insight`). */
  insights?: EnvironmentInsight[] | null
  /** Insight identities THIS user has dismissed (SPA only — per-user rows).
   *  Dismissed lines leave the tile rotation and move to the drawer's
   *  "Dismissed" section. Omit (widget) to hide dismissal entirely. */
  dismissedInsightIds?: string[] | null
  /** Dismiss / restore one insight by identity (SPA only). Both must be
   *  provided for the drawer to offer dismissal. */
  onDismissInsight?: (identity: string) => void
  onRestoreInsight?: (identity: string) => void
  /** THIS user's insight ratings by identity (SPA only — per-user rows).
   *  Omit (widget) to hide the thumbs entirely. */
  insightRatings?: Record<string, InsightRatingState> | null
  /** Rate / clear one insight (SPA only). Both must be provided for the
   *  drawer to offer thumbs. 👎 always carries a reason from the popover. */
  onRateInsight?: (identity: string, thumb: 'up' | 'down', reason?: string) => void
  onUnrateInsight?: (identity: string) => void
  /** Dispatch an on-demand recompute of one lane (SPA only, admin sessions).
   *  Resolves when the lane's computed_at watermark advances (the caller owns
   *  the polling); rejects on 403/429/timeout. Omit (widget) to hide the
   *  refresh buttons. */
  onRunInsights?: (lane: 'fast' | 'slow') => Promise<unknown>
  /** Answer a Layer-2 dependency question (SPA only, admin sessions):
   *  confirm teaches the environment graph the relationship, reject
   *  suppresses it. The edge id comes from the row's evidence payload.
   *  Omit (widget) to hide the Confirm/Reject buttons. */
  onGraphFeedback?: (edgeId: string, action: 'confirm' | 'reject') => Promise<unknown>
  /** Label a situation resolved (SPA only, admin sessions): stores the
   *  resolution into the situation library — future look-alikes cite it —
   *  and optionally corrects the root-cause host. Omit (widget) to hide
   *  the control. */
  onLabelSituation?: (situationId: string, label: string, correctedRoot?: string) => Promise<unknown>
  /** Show the insight tile's "Ask NetWatch →" chat link. Defaults to true;
   *  production builds pass false (VITE_INSIGHT_ASK_ENABLED=0) until the
   *  insight→chat seeding path is exhaustively tested. Display-only gate —
   *  the rest of the tile (popup, drawer, ratings) is unaffected. */
  insightAskEnabled?: boolean
  error?: string | null
  /** Fetch today's detail (for the day drill-down overlay). */
  getToday: () => Promise<DayDetail>
  /** Fetch one compiled past day (the drill-down for non-today cells).
   *  Optional for host-version skew: absent -> past days render their stored
   *  counts without the severity/trend sections. */
  getDay?: (isoDay: string) => Promise<DayHistoryDetail>
  /** Fetch a drive's usage trend + fill-up projection (the disk "Trend" chart).
   *  `days` is the history window (30/60/90/180/365). When omitted, the disk
   *  card falls back to a client-side projection from the row alone. */
  getDiskTrend?: (host: string, drive: string, days: number) => Promise<DiskTrendData>
  /** Open chat seeded with `text` (empty string = open chat blank). */
  onAsk: (text: string) => void
  /** Open chat seeded with `text` in a NEW thread. The per-host "Ask why" uses
   *  this so each investigation starts its own conversation. Falls back to
   *  `onAsk` when omitted. */
  onAskNew?: (text: string) => void
  /** "View hosts" on the Hosts-affected tile. When provided, it takes over the
   *  click (hosts navigate to the incidents view, e.g. `/incidents?type=affected-hosts`);
   *  when omitted, the in-component affected-hosts overlay opens as a fallback. */
  onViewHosts?: () => void
  /** The strip footer's "N overdue" pill. When provided, clicking navigates to
   *  the overdue-filtered incidents view (`/incidents?type=overdue`); when
   *  omitted, the pill seeds a chat investigation instead. */
  onViewOverdue?: () => void
  /** Retry the confidence fetch (shown on error). */
  onRetry?: () => void
  /** Rendered while the confidence data is loading (e.g. the SPA's brand
   *  spinner). Defaults to the "Building your outlook…" hero. */
  loader?: ReactNode
}

// --- sentiment ----------------------------------------------------------------

export type Tone = 'great' | 'ok' | 'warn' | 'bad'

// Score → band, used only where severity data doesn't exist (the 7-day strip's
// historical days + the day drill-down). The LIVE hero/band-strip use
// severityStatus() instead.
export function band(score: number): { label: string; tone: Tone } {
  const p = Math.round(score * 100)
  if (p >= 100) return { label: 'All clear', tone: 'great' }
  if (p >= 85) return { label: 'Minor issues', tone: 'ok' }
  if (p >= 50) return { label: 'Needs attention', tone: 'warn' }
  return { label: 'Critical', tone: 'bad' }
}

// Live hero status from the WORST active problem severity (NOT the score):
// nothing active → All clear; Not classified/Information → Minor issues;
// Warning/Average → Needs attention; High/Disaster → Critical. A null worst
// severity with problems active (severity data still loading / degraded)
// lands mid-scale rather than under- or over-alarming.
export function severityStatus(activeIncidents: number, worstSeverity: number | null): { label: string; tone: Tone } {
  if (activeIncidents === 0) return { label: 'All clear', tone: 'great' }
  if (worstSeverity == null) return { label: 'Needs attention', tone: 'warn' }
  if (worstSeverity >= HIGH_SEVERITY) return { label: 'Critical', tone: 'bad' }
  if (worstSeverity >= 2) return { label: 'Needs attention', tone: 'warn' }
  return { label: 'Minor issues', tone: 'ok' }
}

export const TONE_COLOR: Record<Tone, string> = {
  great: '#46b27e', ok: '#d99a3a', warn: '#dd7a38', bad: '#d9605c',
}

// Labels come from the canonical severity registry — no local copy to drift.
const SEV_LABELS: Record<number, string> = Object.fromEntries(
  ([0, 1, 2, 3, 4, 5] as const).map((s) => [s, SEVERITY[s].label]),
)
const HIGH_SEVERITY = 4 // High (4) + Disaster (5) → "needs a look now"

// --- Hosts-affected severity split -------------------------------------------
// Hosts bucketed by their WORST active problem (payload `affected_by_severity`),
// worst severity first. The worst bucket present drives the condition tile's
// icon, condition word, number colour and urgency sub-line.
interface SevCount { sev: number; count: number }

// Segment class for the tile's stacked severity bar — one colour per level,
// distinct High (salmon) vs Disaster (deep red), mirroring the SEVERITY registry.
const sevBarClass = (sev: number) =>
  sev >= 5 ? 'sv-disaster' : sev === 4 ? 'sv-high' : sev >= 2 ? 'sv-moderate' : sev >= 1 ? 'sv-low' : 'sv-unclassified'

// Tile sub-line urgency phrase from the worst severity present:
// High/Disaster → "act now" (red), Warning/Average → "look soon" (orange),
// below that → "worth a look" (amber).
const urgency = (sev: number): { word: string; cls: string } =>
  sev >= HIGH_SEVERITY ? { word: 'act now', cls: 'urg-red' }
    : sev >= 2 ? { word: 'look soon', cls: 'urg-orange' }
      : { word: 'worth a look', cls: 'urg-amber' }


// Hosts-affected tile condition word + colour class, from the worst
// affected-host severity: 5 Disaster, 4 High, 2–3 Needs attention,
// 0–1 Minor issues. Distinct from the band strip: sev 5 reads "Disaster".
// ("Critical" is the BAND word for High+Disaster — using it here as the sev-4
// label made sev 4 read as more urgent than the scale's own names.)
const condition = (sev: number): { word: string; cls: string } =>
  sev >= 5 ? { word: 'Disaster', cls: 'sev5' }
    : sev === 4 ? { word: 'High', cls: 'sev4' }
      : sev >= 2 ? { word: 'Needs attention', cls: 'orange' }
        : { word: 'Minor issues', cls: 'amber' }


/** Worst-first [{sev, count}] — null when the payload carries no breakdown
 *  (degraded / pre-upgrade cache). */
function severitySplit(bySev?: Record<string, number> | null): SevCount[] | null {
  if (!bySev) return null
  return Object.entries(bySev)
    .map(([sev, count]) => ({ sev: Number(sev), count }))
    .filter((e) => e.count > 0 && Number.isFinite(e.sev))
    .sort((a, b) => b.sev - a.sev)
}

const pct = (s: number) => `${Math.round(s * 100)}%`

// Compact day count for a disk ETA (e.g. "<1d", "~4d", "~120d").
function fmtDays(d: number | null | undefined): string {
  if (d == null) return ''
  if (d < 1) return '<1d'
  return `~${Math.round(d)}d`
}

function relativeSince(epochSeconds: number): string {
  if (!epochSeconds) return 'unknown'
  const secs = Math.max(0, Math.floor(Date.now() / 1000) - epochSeconds)
  if (secs < 90) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 48) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

interface DaySlot {
  key: string
  weekday: string
  monthDay: string
  score: number | null
  opened: number | null
  resolved: number | null
  /** Every problem live at some point that day (resolved + still active at
   *  close, carried-over included). Feeds the day drill-down. */
  total: number | null
  /** Problems opened that day and still unresolved at close (live count for
   *  today) — the number shown on the day cell. */
  newUnresolved: number | null
  /** Worst severity among the day's still-unresolved problems (0..5) ·
   *  -1 = clean · null = unknown (pre-upgrade row → empty cell). */
  worstSev: number | null
  isToday: boolean
}

function buildWeek(
  history: ConfidenceDay[],
  live: ConfidenceData,
  liveWorstSev: number | null,
  liveNewUnresolved: number,
): DaySlot[] {
  const byDay: Record<string, ConfidenceDay> = {}
  for (const d of history) byDay[d.day] = d
  const today = new Date()
  const slots: DaySlot[] = []
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    const iso = d.toISOString().slice(0, 10)
    const isToday = i === 0
    const row = byDay[iso]
    slots.push({
      key: iso,
      weekday: d.toLocaleDateString(undefined, { weekday: 'short' }),
      monthDay: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      // Today's live snapshot wins; older days come from the compiled history row.
      score: isToday ? live.score : row ? row.score : null,
      opened: isToday ? live.today.issues_opened : row ? row.issues_opened : null,
      resolved: isToday ? live.today.issues_resolved : row ? row.issues_resolved : null,
      // Day total = resolved + still active (127 active, none resolved →
      // "0/127"; all cleared → "127/127"). Rows compiled before the EOD
      // rollup stored the still-active count have NO truthful total (resolved
      // includes recoveries of problems carried in from earlier days), so
      // they render as empty cells — the strip starts over from the upgrade.
      total: isToday
        ? live.today.issues_resolved + live.active_incidents
        : row && row.active_at_close != null
          ? row.issues_resolved + row.active_at_close
          : null,
      newUnresolved: isToday ? liveNewUnresolved : row ? row.new_unresolved ?? null : null,
      // Worst severity among TODAY'S still-open problems only — the cell's
      // icon must agree with its count. Carried-over severity lives in the
      // footer's overdue pill, not here.
      worstSev: isToday
        ? (liveNewUnresolved === 0 ? -1 : liveWorstSev)
        : row && row.active_at_close != null ? row.worst_severity ?? null : null,
      isToday,
    })
  }
  return slots
}

// Day-cell glyph: green check when the day closed clean (-1), the severity's
// shaped icon when something was still active at close. Only called for days
// with post-upgrade data (pre-upgrade rows render as empty cells).
function dayGlyph(s: DaySlot): { level: SeverityKey; label: string } {
  if (s.worstSev != null && s.worstSev >= 0) {
    return {
      level: s.worstSev as SeverityKey,
      label: `Worst of this day's unresolved problems: ${SEV_LABELS[s.worstSev] ?? `severity ${s.worstSev}`}`,
    }
  }
  return { level: 'ok', label: 'All problems resolved' }
}

// --- "Overall health" meter ---------------------------------------------------
// User-facing name for the whole-environment confidence score, shown as N/100
// with a letter grade. Thresholds mirror the system prompt so the AI and the
// UI grade identically: A ≥90, B ≥80, C ≥60, D ≥50, F below.
function healthGrade(p: number): { grade: string; cls: string } {
  if (p >= 90) return { grade: 'A', cls: 'g-a' }
  if (p >= 80) return { grade: 'B', cls: 'g-b' }
  if (p >= 60) return { grade: 'C', cls: 'g-c' }
  if (p >= 50) return { grade: 'D', cls: 'g-d' }
  return { grade: 'F', cls: 'g-f' }
}

function InfoIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
    </svg>
  )
}

// Halo colour behind the position dot — follows the grade's hue so the marker
// itself carries state, not just its position on the gradient.
const GRADE_GLOW: Record<string, string> = {
  'g-a': 'rgba(70, 178, 126, .38)',
  'g-b': 'rgba(217, 154, 58, .38)',
  'g-c': 'rgba(221, 122, 56, .38)',
  'g-d': 'rgba(217, 96, 92, .38)',
  'g-f': 'rgba(217, 96, 92, .38)',
}

// Grade boundaries (D/C/B/A) drawn as faint ticks on the track.
const GRADE_TICKS = [50, 60, 80, 90]

function EnvHealth({ score, onAsk }: { score: number; onAsk: (text: string) => void }) {
  const p = Math.round(score * 100)
  const { grade, cls } = healthGrade(p)
  return (
    <div className="envh">
      <div className="envh-head">
        <span className="envh-title">Overall health</span>
        <button
          className="envh-info"
          title="Overall health of the whole environment, 0–100 — ask NetWatch how it's computed"
          aria-label="Ask NetWatch about the overall health score"
          onClick={() => onAsk('Explain my overall health score — what is it based on and why is it at its current value?')}
        >
          <InfoIcon />
        </button>
        <span className="envh-score"><b className={cls}>{p}</b><span className="envh-max">/100</span></span>
        <span className={`envh-grade ${cls}`}>{grade}</span>
      </div>
      <div className="envh-bar">
        {GRADE_TICKS.map((t) => (
          <i key={t} className="envh-tick" style={{ left: `${t}%` }} />
        ))}
        <i
          className="envh-dot"
          style={{ left: `${Math.max(1.5, Math.min(98.5, p))}%`, '--envh-glow': GRADE_GLOW[cls] } as CSSProperties}
        />
      </div>
      <div className="envh-ends"><span>Poor</span><span>Healthy</span></div>
    </div>
  )
}

const BANDS = ['All clear', 'Minor issues', 'Needs attention', 'Critical'] as const
function activeBandIndex(tone: Tone): number {
  if (tone === 'great') return 0
  if (tone === 'ok') return 1
  if (tone === 'warn') return 2
  return 3
}

// --- the live reading, in one place ------------------------------------------
// Status comes from what is actually on fire, never from the score. The live
// problems list is the primary source; the payload's host-level severity split
// covers the gap while it loads (and IS the source on surfaces that don't poll
// problems at all, like the widget's landing summary). Both the full page and
// the compact summary read through here so they can never disagree.
interface LiveReading {
  /** Active problems — live count when available, else the payload's. */
  active: number
  /** Hosts bucketed by their worst active problem, worst first (null = the
   *  payload carries no breakdown). */
  sevSplit: SevCount[] | null
  /** The worst bucket present, or null. */
  worst: SevCount | null
  /** Worst active severity overall; null = active problems but no severity
   *  data yet. */
  worstSev: number | null
  tone: Tone
}

function liveReading(data: ConfidenceData, problems: ActiveProblem[]): LiveReading {
  const active = problems.length > 0 ? problems.length : data.active_incidents
  const sevSplit = severitySplit(data.affected_by_severity)
  const worst = sevSplit && sevSplit.length > 0 ? sevSplit[0] : null
  const worstSev = problems.length > 0
    ? problems.reduce((m, p) => Math.max(m, p.severity), 0)
    : worst ? worst.sev : null
  return { active, sevSplit, worst, worstSev, tone: severityStatus(active, worstSev).tone }
}

// --- icons -------------------------------------------------------------------

function AtomMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 48 48" fill="none">
      <g stroke="#5b93e8">
        <path d="M24 4 L41.3 14 L41.3 34 L24 44 L6.7 34 L6.7 14 Z" strokeWidth="2.6" opacity=".5" />
        <path d="M24 13 L33.5 18.5 L33.5 29.5 L24 35 L14.5 29.5 L14.5 18.5 Z" strokeWidth="2.4" />
      </g>
      <circle cx="24" cy="24" r="3.2" fill="var(--ink)" />
    </svg>
  )
}

// --- AI Insight tile -----------------------------------------------------------

const INSIGHT_ROTATE_MS = 8000
const DAY_MS = 86_400_000

/** Viewer-clock relative hint: "just now" / "12m ago" / "6h ago" / "3d ago". */
function relTime(ms: number): string {
  const d = Math.max(0, Date.now() - ms)
  if (d < 60_000) return 'just now'
  const min = Math.round(d / 60_000)
  if (min < 60) return `${min}m ago`
  const h = Math.round(min / 60)
  if (h < 48) return `${h}h ago`
  const days = Math.round(h / 24)
  if (days < 14) return `${days}d ago`
  return `${Math.round(days / 7)}w ago`
}

/** Absolute anchor text in the client-environment TZ ("02:05" within a day,
 *  "Tue 02:05" within a week, "Jun 3" beyond) — the backup window a line
 *  names must stay on the environment's clock, not the viewer's. Falls back
 *  to the viewer TZ if `tz` is invalid. */
function absAnchor(epoch: number, tz?: string | null): string {
  const dt = new Date(epoch * 1000)
  const age = Date.now() - dt.getTime()
  const fmt = (opts: Intl.DateTimeFormatOptions): string => {
    try {
      return dt.toLocaleString([], tz ? { ...opts, timeZone: tz } : opts)
    } catch {
      return dt.toLocaleString([], opts)
    }
  }
  if (age < DAY_MS) return fmt({ hour: '2-digit', minute: '2-digit', hour12: false })
  if (age < 7 * DAY_MS) {
    return fmt({ weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
  }
  return fmt({ month: 'short', day: 'numeric' })
}

/** Replace {{tN}} tokens with rendered anchors: absolute (client-env TZ) +
 *  relative hint — "at 02:05 (6h ago)". Tooltip carries the viewer-local
 *  full timestamp for cross-timezone operators. */
function renderAnchored(text: string, ins: EnvironmentInsight): ReactNode {
  const anchors = ins.anchors ?? []
  // Tokens are processed even when the anchors array is empty/missing: an
  // unresolvable token renders as '' below — literal {{tN}} braces must never
  // reach the user.
  if (!text.includes('{{t')) return text
  return text.split(/(\{\{t\d+\}\})/g).map((part, i) => {
    const m = /^\{\{t(\d+)\}\}$/.exec(part)
    if (!m) return part
    const a = anchors[Number(m[1])]
    if (!a) return ''
    const when = new Date(a.epoch * 1000)
    return (
      <span
        key={i}
        className="ianc"
        title={`${a.label ? `${a.label} — ` : ''}${when.toLocaleString()} (your time)`}
      >
        {absAnchor(a.epoch, ins.tz)}
        <span className="ianc-rel"> ({relTime(when.getTime())})</span>
      </span>
    )
  })
}

/** Plain-string variant of renderAnchored for text that leaves the UI as raw
 *  text instead of markup — the chat seed. Same "02:05 (6h ago)" wording; a
 *  token with no matching anchor is dropped and punctuation/whitespace tidied,
 *  so literal {{tN}} braces never end up in a chat message. */
function anchorPlain(text: string, ins: EnvironmentInsight): string {
  if (!text.includes('{{t')) return text
  const anchors = ins.anchors ?? []
  return text
    .replace(/\{\{t(\d+)\}\}/g, (_, n) => {
      const a = anchors[Number(n)]
      return a ? `${absAnchor(a.epoch, ins.tz)} (${relTime(a.epoch * 1000)})` : ''
    })
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.;!?])/g, '$1')
    .trim()
}

function isNewInsight(ins: EnvironmentInsight): boolean {
  if (!ins.first_seen) return false
  return Date.now() - new Date(ins.first_seen).getTime() < DAY_MS
}

/** Layer-2 rows are recognized by identity prefix; the ids their admin
 *  actions need ride the evidence payload — that is the API contract
 *  (docs/insights-environment-model.md), so a row without one simply
 *  renders without the action. */
function questionEdgeId(ins: EnvironmentInsight): string | null {
  if (!ins.identity?.startsWith('env_graph_question:')) return null
  const ev = ins.evidence?.env_graph_question as Record<string, unknown> | undefined
  return typeof ev?.edge_id === 'string' && ev.edge_id ? ev.edge_id : null
}

function situationRefId(ins: EnvironmentInsight): string | null {
  if (!ins.identity?.startsWith('situation:')) return null
  const ev = ins.evidence?.situation as Record<string, unknown> | undefined
  return typeof ev?.id === 'string' && ev.id ? ev.id : null
}

/** Any admin-visible row in the payload ⇒ this session passed the server's
 *  admin gate (same heuristic the lane refresh buttons use). */
function hasAdminRows(items: EnvironmentInsight[]): boolean {
  return items.some((i) => i.visibility === 'admin')
}

const LANE_INFO: Record<string, { label: string; cadenceMs: number }> = {
  slow: { label: '6-hour sweep', cadenceMs: 6 * 3_600_000 },
  fast: { label: '15-min security lane', cadenceMs: 15 * 60_000 },
}

/** The freshness "i": when this insight was computed, by which lane, and a
 *  stale warning once the run is over 2× its lane cadence old — the user is
 *  always one glance from knowing how current the box is. */
function FreshnessIcon({ insight }: { insight: EnvironmentInsight }) {
  const [open, setOpen] = useState(false)
  if (!insight.computed_at) return null
  const at = new Date(insight.computed_at)
  const lane = LANE_INFO[insight.lane ?? 'slow'] ?? LANE_INFO.slow
  const stale = Date.now() - at.getTime() > 2 * lane.cadenceMs
  return (
    <span className="ifr" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`ifr-i${stale ? ' stale' : ''}`}
        aria-label="When this insight was generated"
        data-tip={open ? undefined : `Generated ${at.toLocaleString()}`}
        onClick={() => setOpen((o) => !o)}
      >
        i
      </button>
      {open && (
        <span className="ifr-pop" role="note">
          {stale && <span className="ifr-stale">⚠ Stale — the sweep may be behind</span>}
          <span>Generated {at.toLocaleString()} ({relTime(at.getTime())})</span>
          <span>Lane: {lane.label}</span>
          {insight.first_seen && (
            <span>First seen {relTime(new Date(insight.first_seen).getTime())}</span>
          )}
        </span>
      )}
    </span>
  )
}

/** 👎 reason vocabulary — mirrors the backend enum. "Incorrect" additionally
 *  lands in the operator's review queue server-side (a wrong number is a
 *  detector bug, not a preference). */
const RATE_REASONS: Array<[string, string]> = [
  ['not_useful', 'Not useful'],
  ['expected_behavior', 'Expected behavior'],
  ['too_noisy', 'Too noisy'],
  ['incorrect', 'Incorrect'],
]

function ThumbGlyph({ down = false }: { down?: boolean }) {
  return (
    <svg
      width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={down ? { transform: 'rotate(180deg)' } : undefined} aria-hidden
    >
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </svg>
  )
}

/** Per-line 👍/👎 (SPA only — per-user rows). Rating is SIGNAL for the
 *  learning pipe, never a visibility action: nothing hides (dismiss does
 *  that). 👎 opens the reason popover and only lands with a reason picked;
 *  clicking the active thumb again clears the rating. */
function InsightThumbs({ identity, rating, onRate, onUnrate }: {
  identity: string
  rating?: InsightRatingState | null
  onRate: (identity: string, thumb: 'up' | 'down', reason?: string) => void
  onUnrate: (identity: string) => void
}) {
  const [reasonsOpen, setReasonsOpen] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)
  useOutsidePress(wrapRef, () => setReasonsOpen(false), reasonsOpen)
  const cur = rating?.thumb
  return (
    <span className="irate" ref={wrapRef} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`irate-b${cur === 'up' ? ' on' : ''}`}
        aria-label="Helpful"
        aria-pressed={cur === 'up'}
        title={cur === 'up' ? 'Clear your rating' : 'Helpful'}
        onClick={() => {
          setReasonsOpen(false)
          if (cur === 'up') onUnrate(identity)
          else onRate(identity, 'up')
        }}
      >
        <ThumbGlyph />
      </button>
      <button
        type="button"
        className={`irate-b${cur === 'down' ? ' on down' : ''}`}
        aria-label="Not helpful"
        aria-pressed={cur === 'down'}
        aria-expanded={reasonsOpen}
        title={cur === 'down' ? 'Clear your rating' : 'Not helpful'}
        onClick={() => {
          if (cur === 'down') {
            setReasonsOpen(false)
            onUnrate(identity)
          } else {
            setReasonsOpen((o) => !o)
          }
        }}
      >
        <ThumbGlyph down />
      </button>
      {reasonsOpen && (
        <span className="irate-pop" role="menu" aria-label="Why wasn't this helpful?">
          <span className="irate-pop-t">Why wasn't this helpful?</span>
          {RATE_REASONS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              onClick={() => {
                setReasonsOpen(false)
                onRate(identity, 'down', key)
              }}
            >
              {label}
            </button>
          ))}
        </span>
      )}
    </span>
  )
}

/** Display labels for the server-derived family keys; unknown keys fall back
 *  to a prettified form of the key itself. */
const FAMILY_LABELS: Record<string, string> = {
  correlated: 'Correlated',
  ad_events: 'AD events',
  disk_capacity: 'Disk & capacity',
  trends: 'Trends',
  vmware: 'VMware',
  monitoring_hygiene: 'Monitoring hygiene',
  efficiency: 'Efficiency',
  other: 'Patterns',
}

/** Family groups for the drawer: Correlated pinned first, then groups by
 *  worst active severity (tie: most recent computed_at). Rows keep their
 *  incoming (rank) order within a group — grouping is presentation only. */
function groupInsights(items: EnvironmentInsight[]): [string, EnvironmentInsight[]][] {
  const groups = new Map<string, EnvironmentInsight[]>()
  for (const ins of items) {
    const fam = ins.family || 'other'
    const list = groups.get(fam) ?? []
    list.push(ins)
    groups.set(fam, list)
  }
  const worst = (rows: EnvironmentInsight[]) =>
    Math.max(...rows.map((r) => r.severity ?? 0))
  const newest = (rows: EnvironmentInsight[]) =>
    Math.max(...rows.map((r) => (r.computed_at ? new Date(r.computed_at).getTime() : 0)))
  return [...groups.entries()].sort((a, b) => {
    if ((a[0] === 'correlated') !== (b[0] === 'correlated')) {
      return a[0] === 'correlated' ? -1 : 1
    }
    return worst(b[1]) - worst(a[1]) || newest(b[1]) - newest(a[1])
  })
}

/** On-demand lane refresh. The promise resolves when the lane's watermark
 *  advanced (the SPA hook owns polling); 429/403/timeout reject. */
/** Confirm/Reject on a dependency question (admin SPA sessions). Answering
 *  teaches the environment graph immediately; on success the row is also
 *  dismissed for this user so it disappears now — the next sweep drops
 *  answered questions server-side anyway, dismissal just skips the wait. */
function QuestionActions({ edgeId, identity, onFeedback, onDismiss }: {
  edgeId: string
  identity: string
  onFeedback: (edgeId: string, action: 'confirm' | 'reject') => Promise<unknown>
  onDismiss?: (identity: string) => void
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle')
  const [answer, setAnswer] = useState<'confirm' | 'reject' | null>(null)
  async function act(action: 'confirm' | 'reject') {
    if (state === 'busy' || state === 'done') return
    setState('busy')
    setAnswer(action)
    try {
      await onFeedback(edgeId, action)
      setState('done')
      onDismiss?.(identity)
    } catch {
      setState('error')
    }
  }
  if (state === 'done') {
    return (
      <span className="nw-ol-qact-done" role="status">
        {answer === 'confirm' ? 'Learned — thanks ✓' : 'Noted — won’t use it'}
      </span>
    )
  }
  return (
    <span className="nw-ol-qact" onClick={(e) => e.stopPropagation()}>
      {state === 'error' && (
        <span className="nw-ol-qact-err" role="alert">Didn’t save —</span>
      )}
      <button
        type="button"
        className="nw-ol-insrow-restore"
        disabled={state === 'busy'}
        aria-label="Confirm this relationship — the environment model will use it"
        onClick={() => act('confirm')}
      >
        {state === 'busy' && answer === 'confirm' ? 'Saving…' : 'Confirm'}
      </button>
      <button
        type="button"
        className="nw-ol-insrow-restore"
        disabled={state === 'busy'}
        aria-label="Reject this relationship — stop suggesting it"
        onClick={() => act('reject')}
      >
        {state === 'busy' && answer === 'reject' ? 'Saving…' : 'Reject'}
      </button>
    </span>
  )
}

/** One-tap situation labeling (admin SPA sessions): "resolved by X", with an
 *  optional corrected root host. Feeds the situation library — future
 *  look-alikes cite the resolution — and the graph's learning loop. A pill
 *  that expands into a two-field inline form. */
function SituationLabelControl({ situationId, identity, onLabel, onDismiss }: {
  situationId: string
  identity: string
  onLabel: (situationId: string, label: string, correctedRoot?: string) => Promise<unknown>
  onDismiss?: (identity: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [root, setRoot] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle')
  async function save() {
    const trimmed = label.trim()
    if (!trimmed || state === 'busy') return
    setState('busy')
    try {
      await onLabel(situationId, trimmed, root.trim() || undefined)
      setState('done')
      onDismiss?.(identity)
    } catch {
      setState('error')
    }
  }
  if (state === 'done') {
    return (
      <div className="nw-ol-sitform-done" role="status">
        Saved — future look-alike situations will cite this resolution.
      </div>
    )
  }
  if (!open) {
    return (
      <button
        type="button"
        className="nw-ol-insrow-restore"
        onClick={(e) => { e.stopPropagation(); setOpen(true) }}
      >
        Mark resolved…
      </button>
    )
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') save()
    if (e.key === 'Escape') setOpen(false)
  }
  return (
    <div className="nw-ol-sitform" onClick={(e) => e.stopPropagation()}>
      <label className="nw-ol-sitform-l" htmlFor="nw-ol-sit-label">
        How was it resolved?
      </label>
      <input
        id="nw-ol-sit-label"
        className="nw-ol-sitform-in"
        value={label}
        maxLength={200}
        placeholder="e.g. restarted the report service"
        autoFocus
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={onKey}
      />
      <label className="nw-ol-sitform-l" htmlFor="nw-ol-sit-root">
        Actual root-cause host (only if we picked the wrong one)
      </label>
      <input
        id="nw-ol-sit-root"
        className="nw-ol-sitform-in"
        value={root}
        maxLength={120}
        placeholder="host name — leave blank to keep ours"
        onChange={(e) => setRoot(e.target.value)}
        onKeyDown={onKey}
      />
      {state === 'error' && (
        <div className="nw-ol-qact-err" role="alert">Didn’t save — try again.</div>
      )}
      <div className="nw-ol-sitform-b">
        <button
          type="button"
          className="nw-ol-insrow-restore"
          disabled={!label.trim() || state === 'busy'}
          onClick={save}
        >
          {state === 'busy' ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="nw-ol-sitform-cancel"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function RunLaneButton({ lane, onRun }: {
  lane: 'fast' | 'slow'
  onRun: (lane: 'fast' | 'slow') => Promise<unknown>
}) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const label = lane === 'fast' ? 'Refresh security' : 'Refresh patterns'
  return (
    <button
      type="button"
      className="nw-ol-insrun"
      disabled={busy}
      title="Recompute this lane's insights now"
      onClick={async (e) => {
        e.stopPropagation()
        setBusy(true)
        setFailed(false)
        try {
          await onRun(lane)
        } catch {
          setFailed(true)
        } finally {
          setBusy(false)
        }
      }}
    >
      {busy ? 'Refreshing…' : failed ? 'Try again soon' : label}
    </button>
  )
}

/** The full insight box: every active line grouped by family (Correlated
 *  pinned first), NEW badges, per-line freshness, dismiss + thumbs
 *  (SPA-only), per-lane refresh buttons (admin SPA sessions), and the
 *  dismissed section with restore. Clicking a row opens the evidence popup. */
function InsightDrawer({ items, dismissed, onBack, onDismiss, onRestore, ratings, onRate, onUnrate, onRun, onGraphFeedback, onLabelSituation }: {
  items: EnvironmentInsight[]
  dismissed: EnvironmentInsight[]
  onBack: () => void
  onDismiss?: (identity: string) => void
  onRestore?: (identity: string) => void
  ratings?: Record<string, InsightRatingState> | null
  onRate?: (identity: string, thumb: 'up' | 'down', reason?: string) => void
  onUnrate?: (identity: string) => void
  onRun?: (lane: 'fast' | 'slow') => Promise<unknown>
  onGraphFeedback?: (edgeId: string, action: 'confirm' | 'reject') => Promise<unknown>
  onLabelSituation?: (situationId: string, label: string, correctedRoot?: string) => Promise<unknown>
}) {
  const [detailIns, setDetailIns] = useState<EnvironmentInsight | null>(null)
  const isAdmin = hasAdminRows(items)
  if (detailIns) {
    return (
      <InsightDetail
        insight={detailIns}
        onBack={() => setDetailIns(null)}
        canAdmin={isAdmin}
        onGraphFeedback={onGraphFeedback}
        onLabelSituation={onLabelSituation}
        onDismiss={onDismiss}
      />
    )
  }
  const canRun = !!onRun && isAdmin
  const groups = groupInsights(items)
  // One button per LANE (a run recomputes the whole lane, so a per-family
  // button would lie) — rendered on the first group containing that lane.
  const laneShown = new Set<string>()
  return (
    <OverlayDialog
      title={<>AI Insights <span className="ik">{items.length} active</span></>}
      ariaLabel="All AI insights"
      onClose={onBack}
    >
      {items.length === 0 && (
        <div className="nw-ol-muted">No active insights right now.</div>
      )}
      {groups.map(([family, rows]) => {
        const lanes = canRun
          ? (['fast', 'slow'] as const).filter(
              (l) => rows.some((r) => (r.lane ?? 'slow') === l) && !laneShown.has(l),
            )
          : []
        lanes.forEach((l) => laneShown.add(l))
        return (
          <div key={family}>
            <div className="nw-ol-inshead">
              <span className="nw-ol-sect">
                {FAMILY_LABELS[family] ?? family.replace(/_/g, ' ')}
              </span>
              {lanes.map((l) => (
                <RunLaneButton key={l} lane={l} onRun={onRun!} />
              ))}
            </div>
            {rows.map((ins) => {
              const qEdge = isAdmin && onGraphFeedback ? questionEdgeId(ins) : null
              return (
              <div
                key={ins.identity || ins.headline}
                className="nw-ol-insrow"
                onClick={() => setDetailIns(ins)}
              >
                <div className="nw-ol-insrow-top">
                  <span className="ik">{ins.kind}</span>
                  {isNewInsight(ins) && <span className="ik inew">NEW</span>}
                  {ins.computed_at && (
                    <span className="nw-ol-insrow-fresh">
                      {relTime(new Date(ins.computed_at).getTime())}
                    </span>
                  )}
                  {onRate && onUnrate && ins.identity && ins.identity !== 'quiet_state' && (
                    <InsightThumbs
                      identity={ins.identity}
                      rating={ratings?.[ins.identity]}
                      onRate={onRate}
                      onUnrate={onUnrate}
                    />
                  )}
                  {onDismiss && ins.identity && ins.identity !== 'quiet_state' && (
                    <button
                      type="button"
                      className="nw-ol-insrow-x"
                      aria-label="Dismiss this insight"
                      title="Dismiss — it returns automatically if it worsens"
                      onClick={(e) => { e.stopPropagation(); onDismiss(ins.identity!) }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div className="nw-ol-insrow-h">{renderAnchored(ins.headline, ins)}</div>
                {qEdge && ins.identity && (
                  <div className="nw-ol-insrow-acts">
                    <QuestionActions
                      edgeId={qEdge}
                      identity={ins.identity}
                      onFeedback={onGraphFeedback!}
                      onDismiss={onDismiss}
                    />
                  </div>
                )}
              </div>
              )
            })}
          </div>
        )
      })}
      {dismissed.length > 0 && (
        <>
          <div className="nw-ol-sect">Dismissed by you</div>
          {dismissed.map((ins) => (
            <div key={ins.identity || ins.headline} className="nw-ol-insrow dim">
              <div className="nw-ol-insrow-h">{renderAnchored(ins.headline, ins)}</div>
              {onRestore && ins.identity && (
                <button
                  type="button"
                  className="nw-ol-insrow-restore"
                  onClick={() => onRestore(ins.identity!)}
                >
                  Restore
                </button>
              )}
            </div>
          ))}
        </>
      )}
    </OverlayDialog>
  )
}

/** Rotating "AI Insight" card: cycles the mined environment insights every 8s
 *  (paused on hover/focus, dot indicators), with manual paging via ‹ › arrows
 *  and touch swipe. With no mined items it renders the posture one-liner
 *  exactly as before. Detail is always visible (2-line clamp) — hover-gated
 *  content is dead on touch and in the widget. */
function InsightTile({ items, dismissed = [], fallbackText, fallbackAsk, onAsk, askEnabled = true, onDismiss, onRestore, ratings, onRate, onUnrate, onRun, onGraphFeedback, onLabelSituation }: {
  items: EnvironmentInsight[]
  dismissed?: EnvironmentInsight[]
  fallbackText: string
  fallbackAsk: string
  onAsk: (text: string) => void
  askEnabled?: boolean
  onDismiss?: (identity: string) => void
  onRestore?: (identity: string) => void
  ratings?: Record<string, InsightRatingState> | null
  onRate?: (identity: string, thumb: 'up' | 'down', reason?: string) => void
  onUnrate?: (identity: string) => void
  onRun?: (lane: 'fast' | 'slow') => Promise<unknown>
  onGraphFeedback?: (edgeId: string, action: 'confirm' | 'reject') => Promise<unknown>
  onLabelSituation?: (situationId: string, label: string, correctedRoot?: string) => Promise<unknown>
}) {
  const [idx, setIdx] = useState(0)
  const [paused, setPaused] = useState(false)
  // The popup pins the insight it was opened on — no slideshow while reading.
  const [expanded, setExpanded] = useState<EnvironmentInsight | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const touchX = useRef<number | null>(null)
  const n = items.length
  useEffect(() => { if (idx >= n && n > 0) setIdx(0) }, [n, idx])  // list shrank on a poll
  useEffect(() => {
    if (n < 2 || paused || expanded) return
    const id = setInterval(() => setIdx((i) => (i + 1) % n), INSIGHT_ROTATE_MS)
    return () => clearInterval(id)
  }, [n, paused, expanded])

  const step = (d: number) => setIdx((i) => (i + d + n) % n)
  const cur = n > 0 ? items[Math.min(idx, n - 1)] : null
  // anchorPlain: the seed goes to chat as plain text, so {{tN}} tokens must be
  // substituted here — renderAnchored only covers what the tile displays.
  const ask = cur ? anchorPlain(cur.ask || `Tell me more: ${cur.headline}`, cur) : fallbackAsk
  return (
    <>
    {expanded && (
      <InsightDetail
        insight={expanded}
        onBack={() => setExpanded(null)}
        canAdmin={hasAdminRows(items)}
        onGraphFeedback={onGraphFeedback}
        onLabelSituation={onLabelSituation}
        onDismiss={onDismiss}
      />
    )}
    {drawerOpen && (
      <InsightDrawer
        items={items}
        dismissed={dismissed}
        onBack={() => setDrawerOpen(false)}
        onDismiss={onDismiss}
        onRestore={onRestore}
        ratings={ratings}
        onRate={onRate}
        onUnrate={onUnrate}
        onRun={onRun}
        onGraphFeedback={onGraphFeedback}
        onLabelSituation={onLabelSituation}
      />
    )}
    <div
      className="insight"
      onClick={() => (cur ? setExpanded(cur) : onAsk(ask))}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; setPaused(true) }}
      onTouchEnd={(e) => {
        const start = touchX.current
        touchX.current = null
        setPaused(false)
        if (start == null || n < 2) return
        const dx = e.changedTouches[0].clientX - start
        // A real swipe pages (and swallows the click-through); a tap falls
        // through to the tile's onClick → Ask NetWatch.
        if (Math.abs(dx) > 40) {
          e.preventDefault()
          step(dx < 0 ? 1 : -1)
        }
      }}
    >
      <div className="gl"><AtomMark /></div>
      <div className="ib" aria-live="polite">
        <div className="il">
          AI Insight
          {cur && <span className="ik">{cur.kind}</span>}
          {cur && isNewInsight(cur) && <span className="ik inew">NEW</span>}
          {cur && <FreshnessIcon insight={cur} />}
        </div>
        <div className="ih" key={cur ? idx : 'fb'}>
          {cur ? renderAnchored(cur.headline, cur) : fallbackText}
        </div>
        {cur && (cur.enrichment || cur.detail) && (cur.enrichment || cur.detail) !== cur.headline && (
          <div className="id">{renderAnchored(cur.enrichment || cur.detail, cur)}</div>
        )}
        {(n > 0 || dismissed.length > 0) && (
          <div className="ind" onClick={(e) => e.stopPropagation()}>
            {n > 1 && (
              <>
                <button type="button" className="iarr" aria-label="Previous insight" onClick={() => step(-1)}>‹</button>
                {items.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`idot${i === idx ? ' on' : ''}`}
                    aria-label={`Insight ${i + 1} of ${n}`}
                    onClick={() => setIdx(i)}
                  />
                ))}
                <button type="button" className="iarr" aria-label="Next insight" onClick={() => step(1)}>›</button>
              </>
            )}
            <button
              type="button"
              className="iall"
              onClick={() => setDrawerOpen(true)}
            >
              All insights ({n}){dismissed.length > 0 ? ` · ${dismissed.length} dismissed` : ''}
            </button>
          </div>
        )}
      </div>
      {askEnabled && (
        <button
          type="button"
          className="ig"
          onClick={(e) => { e.stopPropagation(); onAsk(ask) }}
        >
          Ask NetWatch →
        </button>
      )}
    </div>
    </>
  )
}

// Human labels for the evidence sections rendered in the insight popup.
const EVIDENCE_LABELS: Record<string, string> = {
  repeat_offenders: 'Repeat offenders (30 days)',
  weekday_skew: 'Time-of-week pattern (60 days)',
  top_hosts: 'Noisiest hosts (30 days)',
  disk_cohort: 'Disks filling',
  sla_laggards: 'Hosts below SLA target',
  long_open: 'Long-open problems',
  posture: '7-day posture',
}

/** Render one evidence value as compact "key: value" lines; arrays of objects
 *  become one line per entry. Generic on purpose — collectors evolve. */
function EvidenceLines({ data }: { data: unknown }) {
  const fmt = (v: unknown): string =>
    Array.isArray(v) ? v.map(fmt).join(', ')
      : typeof v === 'object' && v !== null
        ? Object.entries(v as Record<string, unknown>).filter(([, x]) => x != null)
            .map(([k, x]) => `${k.replace(/_/g, ' ')}: ${fmt(x)}`).join(' · ')
        : String(v)
  if (Array.isArray(data)) {
    return <>{data.map((row, i) => <div key={i} className="nw-ol-evline">{fmt(row)}</div>)}</>
  }
  if (typeof data === 'object' && data !== null) {
    return (
      <>
        {Object.entries(data as Record<string, unknown>).filter(([, v]) => v != null).map(([k, v]) => (
          Array.isArray(v)
            ? <div key={k}>{v.map((row, i) => <div key={i} className="nw-ol-evline">{fmt(row)}</div>)}</div>
            : <div key={k} className="nw-ol-evline">{k.replace(/_/g, ' ')}: {fmt(v)}</div>
        ))}
      </>
    )
  }
  return <div className="nw-ol-evline">{String(data)}</div>
}

// --- contained modal dialog --------------------------------------------------

/** Modal dialog contained to the Outlook area: dimmed backdrop + centered
 *  card with a titled header and × close. Dismissal: ×, Escape, and any press
 *  outside the card — useOutsidePress covers the backdrop, the widget chrome,
 *  and the host page in one mechanism, so the backdrop needs no handler.
 *  Pragmatic a11y by design: role="dialog" + label, no focus trap — which is
 *  also why it makes no aria-modal claim. */
function OverlayDialog({ title, ariaLabel, onClose, children }: {
  title: ReactNode
  ariaLabel: string
  onClose: () => void
  children: ReactNode
}) {
  const cardRef = useRef<HTMLDivElement>(null)
  useOutsidePress(cardRef, onClose)
  // Escape via ref so the listener binds once per mount, not per render.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    function onKey(ev: globalThis.KeyboardEvent) {
      if (ev.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])
  return (
    <div className="nw-ol-dim">
      <div ref={cardRef} className="nw-ol-dialog" role="dialog" aria-label={ariaLabel} onClick={(e) => e.stopPropagation()}>
        <div className="nw-ol-dlg-head">
          <div className="nw-ol-day-title">{title}</div>
          <button type="button" className="nw-ol-dlg-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

/** Dialog for one insight: the fact, why it matters, and the mined
 *  evidence behind it. Read-only — chat hand-off stays on the tile. */
function InsightDetail({ insight, onBack, canAdmin, onGraphFeedback, onLabelSituation, onDismiss }: {
  insight: EnvironmentInsight
  onBack: () => void
  canAdmin?: boolean
  onGraphFeedback?: (edgeId: string, action: 'confirm' | 'reject') => Promise<unknown>
  onLabelSituation?: (situationId: string, label: string, correctedRoot?: string) => Promise<unknown>
  onDismiss?: (identity: string) => void
}) {
  const evidence = (insight.evidence ?? {}) as Record<string, unknown>
  const sections = Object.entries(evidence)
  const qEdge = canAdmin && onGraphFeedback ? questionEdgeId(insight) : null
  const sitId = canAdmin && onLabelSituation ? situationRefId(insight) : null
  return (
    <OverlayDialog
      title={<>AI Insight <span className="ik">{insight.kind}</span></>}
      ariaLabel="AI insight detail"
      onClose={onBack}
    >
      <div className="nw-ol-day-hero">
        <div className="gl"><AtomMark /></div>
        <div>
          <div className="nw-ol-ins-h">{renderAnchored(insight.headline, insight)}</div>
          {(insight.enrichment || insight.detail) && (insight.enrichment || insight.detail) !== insight.headline && (
            <div className="nw-ol-day-label">{renderAnchored(insight.enrichment || insight.detail, insight)}</div>
          )}
          {insight.recommended_action && (
            <div className="nw-ol-ins-action">
              <span className="nw-ol-ins-action-k">Action</span>
              {renderAnchored(insight.recommended_action, insight)}
            </div>
          )}
          {qEdge && insight.identity && (
            <div className="nw-ol-adminact">
              <QuestionActions
                edgeId={qEdge}
                identity={insight.identity}
                onFeedback={onGraphFeedback!}
                onDismiss={onDismiss}
              />
            </div>
          )}
          {sitId && insight.identity && (
            <div className="nw-ol-adminact">
              <SituationLabelControl
                situationId={sitId}
                identity={insight.identity}
                onLabel={onLabelSituation!}
                onDismiss={onDismiss}
              />
            </div>
          )}
        </div>
      </div>
      {sections.length > 0 ? (
        sections.map(([key, data]) => (
          <div key={key}>
            <div className="nw-ol-sect">{EVIDENCE_LABELS[key] ?? key.replace(/_/g, ' ')}</div>
            <div className="nw-ol-evbox"><EvidenceLines data={data} /></div>
          </div>
        ))
      ) : (
        <EmptyState compact kind="search" variant="info" title="No stored evidence"
          body="Nothing was captured for this insight — ask NetWatch in chat for the details." />
      )}
      {insight.computed_at && (
        <div className="nw-ol-muted">
          Generated {new Date(insight.computed_at).toLocaleString()}
          {' '}({relTime(new Date(insight.computed_at).getTime())})
          {' · '}{(LANE_INFO[insight.lane ?? 'slow'] ?? LANE_INFO.slow).label}
          {insight.first_seen && !isNewInsight(insight) && (
            <> · ongoing since {new Date(insight.first_seen).toLocaleDateString()}</>
          )}
        </div>
      )}
    </OverlayDialog>
  )
}

function MoodFace({ tone }: { tone: Tone }) {
  const color = TONE_COLOR[tone]
  const mouth =
    tone === 'great' ? 'M18 33 Q28 41 38 33'
      : tone === 'bad' ? 'M18 37 Q28 30 38 37'
        : 'M19 35 H37'
  return (
    <svg className="moodface" width="46" height="46" viewBox="0 0 56 56" fill="none">
      <circle cx="28" cy="28" r="23" stroke={color} strokeWidth="2.5" fill="var(--card)" />
      <circle cx="20" cy="25" r="2.4" fill={color} />
      <circle cx="36" cy="25" r="2.4" fill={color} />
      <path d={mouth} stroke={color} strokeWidth="2.6" strokeLinecap="round" fill="none" />
    </svg>
  )
}
function ServerArt() {
  return (
    <svg width="140" height="86" viewBox="0 0 200 118" fill="none" stroke="#7e93b0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="40" y="36" width="40" height="62" rx="7" opacity=".4" />
      <rect x="78" y="24" width="50" height="76" rx="9" />
      <path d="M78 46h50M78 66h50M78 84h50" opacity=".45" />
      <circle cx="88" cy="35" r="2.4" fill="#46b27e" stroke="none" />
      <circle cx="88" cy="56" r="2.4" fill="#46b27e" stroke="none" />
      <circle cx="88" cy="75" r="2.4" fill="#d99a3a" stroke="none" />
      <circle cx="150" cy="40" r="6" /><circle cx="166" cy="64" r="6" />
      <path d="M128 54l16-11M128 66l16 8" opacity=".45" />
    </svg>
  )
}
function SendIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M6 11l6-6 6 6" /></svg>
  )
}
function TrendUpIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 7-7" /><path d="M14 7h6v6" /></svg>
  )
}

// --- hero / bands / condition tile (shared by the page and the summary) ------

/** Server art + the "Now" tag + the mood face + the resolved pill. The face is
 *  a LIVE reading (the page's 7-day strip below it is history) — the tag says
 *  so without another line of copy. */
function LiveHero({ tone, resolved }: { tone: Tone; resolved: number }) {
  return (
    <div className="ohero">
      <div className="serverwrap">
        <ServerArt />
        <span className={`nowtag nt-${tone}`} title="Live reading — how the environment looks right now">Now</span>
        <MoodFace tone={tone} />
      </div>
      <div className="ocap">
        <div className="ores">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#46b27e" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
          {' '}{resolved} resolved · last 24h
        </div>
      </div>
    </div>
  )
}

/** The four posture bands, the live one lit. */
function BandStrip({ tone }: { tone: Tone }) {
  const activeIdx = activeBandIndex(tone)
  return (
    <div className="bands">
      {BANDS.map((label, i) => {
        const cls = ['band']
        if (i === activeIdx) cls.push('on', `bt-${tone}`)
        return <div className={cls.join(' ')} key={label}>{label}</div>
      })}
    </div>
  )
}

/** Condition tile (formerly "Hosts affected"): worst-bucket shaped icon +
 *  condition word ("Disaster" for sev 5), the count of hosts AT that severity
 *  (not all affected — "Disaster · 93 hosts" would misread one disaster host
 *  as 93), the stacked severity bar, and an urgency sub-line. Falls back to the
 *  flat "Hosts affected" reading when the payload carries no severity split
 *  (degraded / pre-upgrade cache), and to "All clear" when nothing is affected. */
function ConditionTile({ data, reading, onOpenHosts }: {
  data: ConfidenceData
  reading: LiveReading
  onOpenHosts: () => void
}) {
  const { sevSplit, worst } = reading
  const affected = data.affected_hosts
  // "Urgent" follows the worst HOST's severity when the split is available:
  // one noisy host with three high-severity problems shouldn't paint all
  // affected hosts red. Without a split, fall back to the problems list.
  const hostsUrgent = sevSplit !== null
    ? (worst?.sev ?? 0) >= HIGH_SEVERITY
    : (reading.worstSev ?? 0) >= HIGH_SEVERITY
  return (
    <div className="tile">
      {worst ? (
        <>
          <div className="tl">
            <SeverityIcon level={worst.sev} size={15} title={SEV_LABELS[worst.sev]} />
            {' '}{condition(worst.sev).word}
          </div>
          <div className={`tv tnum ${condition(worst.sev).cls}`}>
            {worst.count}<span className="tsev">host{worst.count === 1 ? '' : 's'}</span>
          </div>
          {sevSplit && sevSplit.length > 0 && (
            <div
              className="sevbar"
              role="img"
              aria-label={sevSplit.map((e) => `${e.count} ${SEV_LABELS[e.sev] ?? `severity ${e.sev}`}`).join(', ')}
            >
              {sevSplit.map((e) => (
                <i
                  key={e.sev}
                  className={sevBarClass(e.sev)}
                  style={{ flexGrow: e.count }}
                  title={`${e.count} host${e.count === 1 ? '' : 's'} · ${SEV_LABELS[e.sev] ?? `severity ${e.sev}`}`}
                />
              ))}
            </div>
          )}
          <div className="ts">
            <span className={`urg ${urgency(worst.sev).cls}`}>{urgency(worst.sev).word}</span> · of <b>{affected}</b> affected
          </div>
        </>
      ) : affected === 0 ? (
        <>
          <div className="tl"><SeverityIcon level="ok" size={15} /> All clear</div>
          <div className="tv tnum">0<span className="tsev">hosts</span></div>
          <div className="ts">no hosts affected</div>
        </>
      ) : (
        <>
          <div className="tl">
            <svg width="15" height="15" viewBox="0 0 32 32" fill="none"><path d="M16 5 L29 27 H3 Z" stroke={hostsUrgent ? '#d9605c' : '#d99a3a'} strokeWidth="2.4" strokeLinejoin="round" /></svg>
            {' '}Hosts affected
          </div>
          <div className={`tv tnum ${hostsUrgent ? 'red' : 'amber'}`}>{affected}</div>
          <div className="ts">of {data.hosts} monitored</div>
        </>
      )}
      <div className="more2" onClick={onOpenHosts}>View hosts →</div>
    </div>
  )
}

// --- day-summary overlay (7-day drill-down) ----------------------------------

// The day modal's trend chart: health (%) on the left axis, problems opened /
// resolved per hour on a shared right axis (two units max, both from 0 — the
// house two-axis guardrails in docs/charts.md). Index tooltip shows all three
// values for the hovered hour. Colors are series tokens validated against
// both surfaces (never status reds — see outlook.css).
function DayTrendChart({ hourly, title, expanded, onExpand, onCloseExpand }: {
  hourly: HourPoint[]
  /** Dialog heading + export filename stem (the modal's section label). */
  title: string
  /** Expanded state lives in DaySummary so the day modal can ignore the
   *  Escape/outside-press that dismissed the chart dialog. */
  expanded: boolean
  onExpand: () => void
  onCloseExpand: () => void
}) {
  const [chart, setChart] = useState<{ instance: ChartJSInstance; surface: string } | null>(null)
  const t = dayTrendSeries(hourly)
  const xs = hourly.map((h) => Math.floor(Date.parse(h.at) / 1000))
  const xMin = Math.min(...xs)
  const xMax = Math.max(...xs)
  const hourTick = (x: number) =>
    new Date(x * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  const base = { data: [], dashed: false, muted: false }
  const series = [
    { ...base, label: 'Health', points: t.health, unit: '%', axis: 'y' as const, colorVar: '--accent', fallback: '#5b93e8', fillAlpha: 0.08 },
    ...(t.opened
      ? [{ ...base, label: 'Opened', points: t.opened, unit: '', axis: 'y1' as const, colorVar: '--chart-measured', fallback: '#c08430', fillAlpha: 0 }]
      : []),
    ...(t.resolved
      ? [{ ...base, label: 'Resolved', points: t.resolved, unit: '', axis: 'y1' as const, colorVar: '--chart-third', fallback: '#9085e9', fillAlpha: 0 }]
      : []),
  ]
  const spec: ChartSpec = {
    type: 'line',
    title,
    unit: '%',
    threshold: null,
    categorical: false,
    labels: [],
    series,
    units: t.opened ? ['%', ''] : ['%'],
    stacked: false,
    horizontal: false,
    xLabel: '',
    yLabel: '',
    // Percent axis pinned to 100 so slopes read truthfully; the counts axis
    // scales to its own peak with head-room (min 4 keeps 0/1 days readable).
    yMax: 100,
    suggestedMax: null,
    suggestedMaxRight: t.opened ? Math.max(4, Math.ceil(t.maxCount * 1.2)) : null,
    // timed drives the CSV export's ISO time column; ticks still come from
    // xTickFormat, which overrides the built-in window formatting.
    timed: true,
    xSpan: xMax - xMin,
    xTickFormat: hourTick,
    xTickValues: xs.length > 2 ? [xMin, xs[Math.floor(xs.length / 2)], xMax] : [xMin, xMax],
    lastValueLabel: false,
    legacy: false,
    height: 170,
    notes: [],
  }
  return (
    <div className="nw-ol-daychart">
      <div className="nw-ol-daychart-head">
        <ChartActions spec={spec} chart={chart} onExpand={onExpand} />
      </div>
      <NwChart
        spec={spec}
        zoomable
        onReady={(instance, surface) => setChart({ instance, surface })}
        ariaLabel="Health, opened and resolved problems through the day"
        height={170}
      />
      <div className="nw-ol-daychart-hint">Drag to zoom · double-click to reset</div>
      {expanded && <ChartDialog spec={spec} onClose={onCloseExpand} />}
    </div>
  )
}

// One day's pop-up summary. Every day shows the same layout: score hero,
// the resolved/total split, four stat boxes (opened / resolved / still open /
// overdue) and the severity + trend sections. Today is served live
// (getToday); past days come from the compiled daily row (getDay), which the
// backend backfills from Zabbix event history for pre-upgrade rows. A host
// that doesn't pass getDay yet degrades past days to the stored counts.
function DaySummary({ slot, getToday, getDay, todayOverdue, onBack }: {
  slot: DaySlot
  getToday: () => Promise<DayDetail>
  getDay?: (isoDay: string) => Promise<DayHistoryDetail>
  /** Live carried-over count (the footer pill's number) for the today modal. */
  todayOverdue: number
  onBack: () => void
}) {
  const isToday = slot.isToday
  const [detail, setDetail] = useState<DayDetail | null>(null)
  const [past, setPast] = useState<DayHistoryDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  // While the trend chart's expanded dialog is open, the Escape/outside-press
  // that dismisses IT must not also dismiss this day modal underneath — both
  // listen on the document. The ref is read synchronously inside the same
  // keydown, before React flushes the chart dialog's own close.
  const [chartExpanded, setChartExpanded] = useState(false)
  const chartExpandedRef = useRef(false)
  chartExpandedRef.current = chartExpanded

  useEffect(() => {
    let alive = true
    const fail = (e: unknown) => alive && setErr((e as Error)?.message || 'Failed to load')
    if (isToday) getToday().then((d) => alive && setDetail(d)).catch(fail)
    else if (getDay) getDay(slot.key).then((d) => alive && setPast(d)).catch(fail)
    return () => { alive = false }
  }, [isToday, getToday, getDay, slot.key])

  const score = slot.score ?? 0
  const b = band(score)
  // Prefer the fetched detail (live for today, compiled for past days); the
  // strip's slot is the pre-fetch fallback so the counts render immediately.
  const loaded = isToday ? detail : past
  const opened = (isToday ? detail?.issues_opened : past?.issues_opened) ?? slot.opened ?? 0
  const resolved = (isToday ? detail?.issues_resolved : past?.issues_resolved) ?? slot.resolved ?? 0
  // Still-active at close from the slot until the fetch lands (slot.total is
  // resolved + active by construction).
  const slotActive = slot.total != null ? Math.max(0, slot.total - (slot.resolved ?? 0)) : null
  const activeAtClose = isToday
    ? (detail ? detail.active_incidents : slotActive)
    : (past ? past.active_at_close : slotActive)
  const counts = dayModalCounts({
    opened,
    resolved,
    activeAtClose,
    newUnresolved: (isToday ? undefined : past?.new_unresolved) ?? slot.newUnresolved,
  })
  // Today's overdue is live (the footer pill's count); a past day's was
  // computed at close (server value, falling back to the same derivation).
  const overdue = isToday ? todayOverdue : past?.overdue_at_close ?? counts.overdue
  const title = isToday ? 'Today’s summary' : `${slot.weekday} · ${slot.monthDay}`
  const when = isToday ? 'right now' : 'end of day'
  const breakdown = isToday ? detail?.severity_breakdown : past?.severity_breakdown
  const sevKeys = breakdown ? [5, 4, 3, 2, 1, 0].filter((k) => breakdown[k]) : []
  const trend = (isToday ? detail?.trend : past?.trend) ?? []
  // Chart input: the hourly series when the backend serves it; otherwise the
  // score-only trend re-shaped (pre-upgrade backend — health line alone).
  const loadedHourly = (isToday ? detail?.hourly : past?.hourly) ?? []
  const hourly: HourPoint[] = loadedHourly.length > 0
    ? loadedHourly
    : trend
        .filter((pt): pt is { at: string; score: number } => pt.at != null)
        .map((pt) => ({ at: pt.at, score: pt.score, opened: null, resolved: null }))
  // Past days without a getDay host wiring have nothing to load — the modal
  // shows the stored counts only, no spinner.
  const fetching = !err && !loaded && (isToday || !!getDay)

  return (
    <OverlayDialog title={title} ariaLabel={`${title} summary`} onClose={() => { if (!chartExpandedRef.current) onBack() }}>
      <div className="nw-ol-day-hero">
        <MoodFace tone={b.tone} />
        <div>
          <div className="nw-ol-day-score">{pct(score)}</div>
          <div className="nw-ol-day-label">{b.label} · {when}</div>
        </div>
      </div>

      {/* Day total = resolved + still open at close (carried-over included) —
          never the same-day opened count, whose recoveries-of-older-problems
          overlap lets resolved/opened exceed 1 ("173/170"). */}
      <div className="nw-ol-day-resolved">
        <b>{counts.total != null ? `${resolved}/${counts.total}` : resolved}</b> problems resolved
      </div>

      <div className="nw-ol-day-stats">
        <div className="nw-ol-stat"><div className="n">{opened}</div><div className="c">opened</div></div>
        <div className="nw-ol-stat"><div className="n">{resolved}</div><div className="c">resolved</div></div>
        <div className="nw-ol-stat"><div className="n">{counts.stillOpen ?? '–'}</div><div className="c">{isToday ? 'still active' : 'open at close'}</div></div>
        <div className="nw-ol-stat"><div className={`n${overdue ? ' bad' : ''}`}>{overdue ?? '–'}</div><div className="c">overdue</div></div>
      </div>

      {err && <div className="nw-ol-muted">⚠ {err}</div>}
      {fetching && <div className="nw-ol-muted">Loading…</div>}
      {loaded && (
        <>
          <div className="nw-ol-sect">{isToday ? 'Active by severity' : 'Open at close by severity'}</div>
          {breakdown == null ? (
            <div className="nw-ol-muted">Severity detail isn’t available for this day.</div>
          ) : sevKeys.length === 0 ? (
            <EmptyState compact kind="shield" variant="positive"
              title={isToday ? 'No active issues' : 'Closed clean'}
              body={isToday
                ? 'Everything is quiet right now.'
                : 'Nothing was still open when this day ended.'} />
          ) : (
            sevKeys.map((k) => (
              <div className="nw-ol-sev" key={k}>
                <SeverityIcon level={k} size={14} title={SEV_LABELS[k]} />
                <span className="nm">{SEV_LABELS[k]}</span>
                <span className="ct">{breakdown[k]}</span>
              </div>
            ))
          )}
          <div className="nw-ol-sect" style={{ marginTop: 14 }}>{isToday ? 'Today’s trend' : 'Score through the day'}</div>
          {hourly.length > 0 ? (
            <DayTrendChart
              hourly={hourly}
              title={isToday ? 'Today’s trend' : `Day trend · ${slot.weekday} ${slot.monthDay}`}
              expanded={chartExpanded}
              onExpand={() => setChartExpanded(true)}
              onCloseExpand={() => setChartExpanded(false)}
            />
          ) : (
            <div className="nw-ol-muted">Hourly trend appears once snapshots accrue.</div>
          )}
        </>
      )}
    </OverlayDialog>
  )
}

// --- affected-hosts overlay (the "Hosts affected" tile drill-down) ----------

interface HostGroup { host: string; worstSeverity: number; problems: ActiveProblem[] }

function groupProblemsByHost(problems: ActiveProblem[]): HostGroup[] {
  const map = new Map<string, ActiveProblem[]>()
  for (const p of problems) {
    const arr = map.get(p.host)
    if (arr) arr.push(p)
    else map.set(p.host, [p])
  }
  const groups: HostGroup[] = []
  for (const [host, ps] of map) {
    ps.sort((a, b) => b.severity - a.severity || b.since - a.since)
    groups.push({ host, worstSeverity: ps[0]?.severity ?? 0, problems: ps })
  }
  groups.sort((a, b) => b.worstSeverity - a.worstSeverity || a.host.localeCompare(b.host))
  return groups
}

const HOSTS_PER_PAGE = 6

function HostsAffected({ problems, onAsk, onAskNew, onBack }: {
  problems: ActiveProblem[]
  onAsk: (text: string) => void
  onAskNew?: (text: string) => void
  onBack: () => void
}) {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const groups = groupProblemsByHost(problems)
  const q = query.trim().toLowerCase()
  const filtered = q
    ? groups.filter((g) =>
        g.host.toLowerCase().includes(q) ||
        g.problems.some((p) => p.name.toLowerCase().includes(q)))
    : groups
  const pageCount = Math.max(1, Math.ceil(filtered.length / HOSTS_PER_PAGE))
  const current = Math.min(page, pageCount)
  const visible = filtered.slice((current - 1) * HOSTS_PER_PAGE, current * HOSTS_PER_PAGE)

  return (
    <OverlayDialog
      title={<>{groups.length} affected host{groups.length === 1 ? '' : 's'}</>}
      ariaLabel="Affected hosts"
      onClose={onBack}
    >
      <label className="nw-ol-hosts-search">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
        <input
          type="text"
          placeholder="Search hosts or problems…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPage(1) }}
        />
      </label>
      {filtered.length === 0 ? (
        <div className="nw-ol-muted">{groups.length === 0 ? 'No affected hosts right now 🎉' : 'No hosts match your search.'}</div>
      ) : (
        <>
          <div className="nw-ol-hostlist">
            {visible.map((g) => <HostCard key={g.host} group={g} onAsk={onAsk} onAskNew={onAskNew} />)}
          </div>
          {pageCount > 1 && (
            <div className="nw-ol-pager">
              <button onClick={() => setPage(current - 1)} disabled={current <= 1}>‹ Prev</button>
              <span>Page {current} of {pageCount}</span>
              <button onClick={() => setPage(current + 1)} disabled={current >= pageCount}>Next ›</button>
            </div>
          )}
        </>
      )}
    </OverlayDialog>
  )
}

function HostCard({ group, onAsk, onAskNew }: {
  group: HostGroup
  onAsk: (text: string) => void
  onAskNew?: (text: string) => void
}) {
  const worst = group.problems[0]
  // "Ask why" opens a fresh chat seeded to investigate this host — the chat's
  // own streaming UI carries the wait (no inline loading screen).
  const seed =
    `Investigate why ${group.host} is alerting` +
    (worst ? ` — find the root cause of “${worst.name}”` : '') +
    ', whether it correlates with other hosts, and how often it has recurred.'

  return (
    <div className="nw-ol-host">
      <div className="nw-ol-host-head">
        <SeverityIcon level={group.worstSeverity} size={14} title={SEV_LABELS[group.worstSeverity]} />
        <span className="nw-ol-host-name">{group.host}</span>
        <span className="nw-ol-host-count">{group.problems.length} problem{group.problems.length === 1 ? '' : 's'}</span>
      </div>
      <div className="nw-ol-host-probs">
        {group.problems.map((p, i) => (
          <div className="nw-ol-host-prob" key={p.eventid ?? i}>
            <SeverityIcon level={p.severity} size={12} title={SEV_LABELS[p.severity]} />
            <span className="nm">{p.name}</span>
            <span className="ago">{relativeSince(p.since)}</span>
          </div>
        ))}
      </div>
      <button className="nw-ol-why-btn" onClick={() => (onAskNew ?? onAsk)(seed)}>
        <AtomMark /> Ask why
      </button>
    </div>
  )
}

// --- filling-disks overlay (the "Capacity trend" tile drill-down) ------------

const DISKS_PER_PAGE = 6

function diskUsage(d: FillingDisk): string {
  if (d.pct != null) return `${Math.round(d.pct)}% used`
  if (d.free_gb != null) return `${Math.round(d.free_gb)} GB free`
  return 'filling'
}

// Compact ETA phrase shared by the tile + the list ("~7d to full").
function diskEta(d: FillingDisk): string {
  const eta = d.days_to_full ?? d.days_to_threshold
  if (eta == null) return 'timing unsure'
  return `${fmtDays(eta)} ${d.days_to_full != null ? 'to full' : 'to threshold'}`
}

function FillingDisks({ disks, getDiskTrend, onBack }: {
  disks: FillingDisk[]
  getDiskTrend?: (host: string, drive: string, days: number) => Promise<DiskTrendData>
  onBack: () => void
}) {
  const [page, setPage] = useState(1)
  const pageCount = Math.max(1, Math.ceil(disks.length / DISKS_PER_PAGE))
  const current = Math.min(page, pageCount)
  const visible = disks.slice((current - 1) * DISKS_PER_PAGE, current * DISKS_PER_PAGE)

  return (
    <OverlayDialog
      title={<>{disks.length} filling disk{disks.length === 1 ? '' : 's'}</>}
      ariaLabel="Filling disks"
      onClose={onBack}
    >
      {disks.length === 0 ? (
        <EmptyState kind="disk" variant="positive" title="No disks filling up"
          body="No drive is trending toward full right now." />
      ) : (
        <>
          <div className="nw-ol-hostlist">
            {visible.map((d, i) => <DiskCard key={`${d.host}:${d.drive}:${i}`} disk={d} getDiskTrend={getDiskTrend} />)}
          </div>
          {pageCount > 1 && (
            <div className="nw-ol-pager">
              <button onClick={() => setPage(current - 1)} disabled={current <= 1}>‹ Prev</button>
              <span>Page {current} of {pageCount}</span>
              <button onClick={() => setPage(current + 1)} disabled={current >= pageCount}>Next ›</button>
            </div>
          )}
        </>
      )}
    </OverlayDialog>
  )
}

// Forward fill projection drawn straight from the row's forecast — the line the
// drive is already on, no extra fetch. The dotted line continues the forecast's
// OWN rate all the way to full (100% used / 0 GB free): when the row carries
// only a threshold ETA, the same slope is extended past the threshold — never
// re-anchored so "full" lands at the threshold date (that would fabricate a
// steeper line than the fit). Returns null when there is no timed ETA at all.
function DiskProjection({ disk }: { disk: FillingDisk }) {
  const isPct = disk.pct != null
  const start = isPct ? (disk.pct as number) : disk.free_gb ?? null
  if (start == null) return null

  const fullY = isPct ? 100 : 0
  const thrY = isPct ? 90 : 5 // % warning line / free-GB floor (mirrors the sweep)
  let eta = disk.days_to_full != null && disk.days_to_full > 0 ? disk.days_to_full : null
  if (eta == null && disk.days_to_threshold != null && disk.days_to_threshold > 0) {
    const thrDist = thrY - start
    const fullDist = fullY - start
    if (thrDist * fullDist > 0) eta = disk.days_to_threshold * (fullDist / thrDist)
  }
  if (eta == null) return null

  const unit = isPct ? '%' : ' GB'
  return (
    <div className="nw-ol-proj">
      <NwLineChart
        ariaLabel={`Projected fill for ${disk.drive} on ${disk.host}`}
        series={[{
          label: 'Projected',
          points: [{ x: 0, y: start }, { x: eta, y: fullY }],
          colorVar: '--accent',
          fallback: '#5b93e8',
          dashed: true,
        }]}
        unit={unit}
        yMax={isPct ? 100 : undefined}
        threshold={isPct ? { y: thrY, label: `${thrY}%` } : null}
        xTick={(v) => (v <= 0 ? 'now' : `full ${fmtDays(v)}`)}
        xTickValues={[0, eta]}
        height={104}
      />
    </div>
  )
}

// Zoom windows for the trend chart. History widens with the window; the dotted
// prediction always runs to fill-up regardless of the selected window.
const TREND_ZOOMS: { d: number; label: string }[] = [
  { d: 30, label: '30d' },
  { d: 60, label: '60d' },
  { d: 90, label: '90d' },
  { d: 180, label: '6mo' },
  { d: 365, label: '1yr' },
]

function fmtDate(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// The plotted chart: real history as a SOLID line, the projection to fill-up as
// a DOTTED line, on one absolute-time x-axis (history start → projected full).
// The projection's endpoint comes straight from the API, whose slope equals the
// fitted rate — the dotted line reads as a continuation of the measured trend
// (and ends below full only when the fill date is past the server's sanity cap).
function TrendSvg({ data, disk }: { data: DiskTrendData; disk: FillingDisk }) {
  const hist = data.history
  const fc = data.forecast
  const all = [...hist, ...fc]
  if (all.length === 0) {
    return <EmptyState compact kind="trend" variant="info" title="No trend data yet"
      body="The trend appears once enough disk history is collected." />
  }

  const isPct = data.unit === '%'
  const unit = isPct ? '%' : ' GB'
  const xMin = Math.min(...all.map((p) => p.t))
  const xMax = Math.max(...all.map((p) => p.t))
  const nowT = fc[0]?.t ?? xMax
  const last = fc[fc.length - 1]
  const fillReached = last != null && (isPct ? last.y >= 100 : last.y <= 0)

  const series: NwSeries[] = []
  if (hist.length) {
    series.push({
      label: 'Measured',
      points: hist.map((p) => ({ x: p.t, y: p.y })),
      colorVar: '--chart-measured',
      fallback: '#c08430',
      fillAlpha: 0.1,
    })
  }
  if (fc.length) {
    series.push({
      label: 'Projected',
      points: fc.map((p) => ({ x: p.t, y: p.y })),
      colorVar: '--accent',
      fallback: '#5b93e8',
      dashed: true,
    })
  }

  return (
    <NwLineChart
      ariaLabel={`Usage trend and projected fill for ${disk.drive} on ${disk.host}`}
      series={series}
      unit={unit}
      yMax={isPct ? 100 : undefined}
      threshold={typeof data.threshold === 'number' ? { y: data.threshold, label: `${data.threshold}%` } : null}
      nowX={nowT}
      xTick={(v) => {
        if (v === nowT) return 'now'
        if (v === xMax && last && v === last.t) return fillReached ? `full ${fmtDate(v)}` : fmtDate(v)
        return fmtDate(v)
      }}
      xTickValues={xMin === nowT ? [nowT, xMax] : [xMin, nowT, xMax]}
      height={132}
    />
  )
}

// "Trend" drill-down: fetches a drive's real usage history (solid) + projected
// fill-up (dotted) and lets the user zoom the history window. Falls back to the
// client-side DiskProjection when the fetch yields no real history.
function DiskTrendChart({ disk, getDiskTrend }: {
  disk: FillingDisk
  getDiskTrend: (host: string, drive: string, days: number) => Promise<DiskTrendData>
}) {
  const [days, setDays] = useState(30)
  const [data, setData] = useState<DiskTrendData | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setErr(null)
    getDiskTrend(disk.host, disk.drive, days)
      .then((d) => { if (alive) { setData(d); setLoading(false) } })
      .catch((e) => { if (alive) { setErr((e as Error)?.message || 'Failed to load'); setLoading(false) } })
    return () => { alive = false }
  }, [disk.host, disk.drive, days, getDiskTrend])

  return (
    <div className="nw-ol-proj">
      <div className="nw-ol-seg" role="group" aria-label="Trend window">
        {TREND_ZOOMS.map((z) => (
          <button key={z.d} type="button" className={`nw-ol-seg-btn${days === z.d ? ' is-active' : ''}`}
                  aria-pressed={days === z.d} onClick={() => setDays(z.d)}>{z.label}</button>
        ))}
      </div>
      {loading && !data ? (
        <div className="nw-ol-proj-msg">Loading trend…</div>
      ) : err ? (
        // Backend unreachable — show the row-derived projection so the toggle
        // always renders something useful.
        <DiskProjection disk={disk} />
      ) : data && data.available ? (
        <TrendSvg data={data} disk={disk} />
      ) : data ? (
        <>
          <TrendSvg data={data} disk={disk} />
          <div className="nw-ol-proj-msg">History unavailable — showing the projection only.</div>
        </>
      ) : null}
    </div>
  )
}

function DiskCard({ disk, getDiskTrend }: {
  disk: FillingDisk
  getDiskTrend?: (host: string, drive: string, days: number) => Promise<DiskTrendData>
}) {
  const urgent = (disk.days_to_threshold ?? disk.days_to_full ?? 99) <= 7
  const fill = disk.pct != null ? Math.max(4, Math.min(100, disk.pct)) : 100
  // Disks don't need an investigation — the trend IS the answer. The toggle
  // reveals real usage history + the projection to fill-up (or, with no trend
  // fetcher wired, the client-side projection alone).
  const [open, setOpen] = useState(false)
  const canProject = (disk.days_to_full ?? disk.days_to_threshold ?? null) != null

  return (
    <div className="nw-ol-host">
      <div className="nw-ol-host-head">
        <span className="dot" style={{ background: urgent ? 'var(--red)' : 'var(--amber)' }} />
        <span className="nw-ol-host-name is-disk">{disk.host}</span>
        <span className="nw-ol-disk-drive">{disk.drive}{disk.recent_jump ? ' ⚡' : ''}</span>
      </div>
      <div className="nw-ol-disk-meter"><div className={`meter${urgent ? ' red' : ''}`}><i style={{ width: `${fill}%` }} /></div></div>
      <div className="nw-ol-host-probs">
        <div className="nw-ol-host-prob">
          <span className="nm">{diskUsage(disk)}{disk.total_gb != null ? ` · ${disk.total_gb} GB` : ''}</span>
          <span className="ago">{diskEta(disk)}</span>
        </div>
      </div>
      {canProject && (
        <button className="nw-ol-why-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <TrendUpIcon /> {open ? 'Hide trend' : 'Trend'}
        </button>
      )}
      {open && (getDiskTrend
        ? <DiskTrendChart disk={disk} getDiskTrend={getDiskTrend} />
        : <DiskProjection disk={disk} />)}
    </div>
  )
}

// --- "Capacity trend" tile ----------------------------------------------------

function DiskIcon({ stroke }: { stroke: string }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="10" rx="2" /><circle cx="12" cy="12" r="2.6" /></svg>
  )
}

// Single worst filling drive + a link into the full list (the chosen layout).
// null disks = loading; [] = calm (nothing filling). Colours come from CSS tokens.
function DriveFillTile({ disks, onOpenDisks }: { disks: FillingDisk[] | null | undefined; onOpenDisks: () => void }) {
  if (disks == null) {
    return (
      <div className="tile">
        <div className="tl"><DiskIcon stroke="var(--amber)" />{' '}Capacity trend</div>
        <div className="tv2"><b>—</b></div>
        <div className="meter"><i style={{ width: '0%' }} /></div>
        <div className="ts">checking disk trends…</div>
      </div>
    )
  }
  if (disks.length === 0) {
    return (
      <div className="tile">
        <div className="tl"><DiskIcon stroke="var(--green)" />{' '}Capacity trend</div>
        <div className="tv2"><b>No disks filling up</b></div>
        <div className="meter ok"><i style={{ width: '100%' }} /></div>
        <div className="ts">nothing on a path to fill</div>
      </div>
    )
  }
  const worst = disks[0]
  const urgent = (worst.days_to_threshold ?? worst.days_to_full ?? 99) <= 7
  const fill = worst.pct != null ? Math.max(4, Math.min(100, worst.pct)) : 100
  const more = disks.length - 1
  return (
    <div className="tile nw-ol-tile-click" onClick={onOpenDisks} role="button" tabIndex={0}>
      <div className="tl"><DiskIcon stroke={urgent ? 'var(--red)' : 'var(--amber)'} />{' '}Capacity trend</div>
      <div className="tv2">
        <b>{worst.host}</b> {worst.drive}
        {worst.recent_jump && <span className="nw-ol-jump" title="Recent spike — filling far above its normal rate">⚡</span>}
      </div>
      <div className={`meter${urgent ? ' red' : ''}`}><i style={{ width: `${fill}%` }} /></div>
      <div className="ts">{diskUsage(worst)} · {diskEta(worst)}</div>
      <div className="more2">
        {more > 0 ? `+${more} more filling →` : 'View details →'}
      </div>
    </div>
  )
}

// --- the component -----------------------------------------------------------

export function Outlook(props: OutlookProps) {
  const { confidence: data, history, problems, disks, error, getToday, getDay, getDiskTrend, onAsk, onAskNew, onRetry, onViewHosts, loader } = props
  const [query, setQuery] = useState('')
  const [daySel, setDaySel] = useState<DaySlot | null>(null)
  const [hostsOpen, setHostsOpen] = useState(false)
  const [disksOpen, setDisksOpen] = useState(false)

  function send() {
    onAsk(query.trim())
    setQuery('')
  }
  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') send()
  }

  return (
    <div className="nw-ol">
      {!data ? (
        error ? (
          <div className="ohero">
            <div className="ocap">
              <div className="om">Couldn’t load your outlook</div>
              <div className="os">{error}</div>
            </div>
            {onRetry && <button className="o-retry" onClick={onRetry}>Retry</button>}
          </div>
        ) : (
          loader ?? (
            <div className="ohero">
              <div className="serverwrap"><ServerArt /></div>
              <div className="ocap"><div className="os">Building your outlook…</div></div>
            </div>
          )
        )
      ) : (
        <Loaded data={data} history={history} problems={problems} disks={disks} insights={props.insights} dismissedInsightIds={props.dismissedInsightIds} onDismissInsight={props.onDismissInsight} onRestoreInsight={props.onRestoreInsight} insightRatings={props.insightRatings} onRateInsight={props.onRateInsight} onUnrateInsight={props.onUnrateInsight} onRunInsights={props.onRunInsights} onGraphFeedback={props.onGraphFeedback} onLabelSituation={props.onLabelSituation} insightAskEnabled={props.insightAskEnabled} onAsk={onAsk} onOpenHosts={onViewHosts ?? (() => setHostsOpen(true))} onViewOverdue={props.onViewOverdue} onOpenDisks={() => setDisksOpen(true)} onOpenDay={setDaySel} />
      )}

      <div className="composer">
        <AtomMark />
        <input type="text" placeholder="Ask NetWatch…" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown} />
        <button className="send" onClick={send} aria-label="Send"><SendIcon /></button>
      </div>

      {daySel && (
        <DaySummary
          slot={daySel}
          getToday={getToday}
          getDay={getDay}
          // Live carried-over count for the today modal — same filter as the
          // footer's overdue pill (opened before today's local midnight).
          todayOverdue={problems.filter((p) => p.since > 0 && p.since < new Date().setHours(0, 0, 0, 0) / 1000).length}
          onBack={() => setDaySel(null)}
        />
      )}
      {hostsOpen && (
        <HostsAffected
          problems={problems}
          onAsk={onAsk}
          onAskNew={onAskNew}
          onBack={() => setHostsOpen(false)}
        />
      )}
      {disksOpen && (
        <FillingDisks
          disks={disks ?? []}
          getDiskTrend={getDiskTrend}
          onBack={() => setDisksOpen(false)}
        />
      )}
    </div>
  )
}

// --- compact summary (the widget's chat landing) -----------------------------

export interface OutlookSummaryProps {
  /** The live environment score (null while loading / on error). */
  confidence: ConfidenceData | null
  /** Live active problems, when the host polls them. Omit and the reading
   *  comes from the payload's `affected_by_severity` — the same fallback the
   *  full page uses while its problems list loads. */
  problems?: ActiveProblem[]
  error?: string | null
  /** Open the full Outlook (the widget switches to its Outlook tab). */
  onOpen: () => void
  /** The condition tile's "View hosts →". Omit to fall back to `onOpen`. */
  onViewHosts?: () => void
  /** Retry the confidence fetch (shown on error). */
  onRetry?: () => void
}

/**
 * The Outlook's first screen, condensed: the live hero, the four posture bands
 * and the condition tile. Everything else — Overall health, capacity, AI
 * insights, the 7-day strip — stays on the full `<Outlook>`, one tap away
 * through `onOpen`.
 *
 * Shares `Outlook`'s derivation (`liveReading`) and its stylesheet, so a
 * surface showing this card can never disagree with the page about what the
 * environment looks like right now.
 */
export function OutlookSummary({ confidence: data, problems = [], error, onOpen, onViewHosts, onRetry }: OutlookSummaryProps) {
  if (!data) {
    return (
      <div className="nw-ol nw-olsum">
        {error ? (
          <div className="ohero">
            <div className="ocap">
              <div className="om">Couldn’t load your outlook</div>
              <div className="os">{error}</div>
            </div>
            {onRetry && <button className="o-retry" onClick={onRetry}>Retry</button>}
          </div>
        ) : (
          <div className="ohero">
            <div className="serverwrap"><ServerArt /></div>
            <div className="ocap"><div className="os">Building your outlook…</div></div>
          </div>
        )}
      </div>
    )
  }
  const reading = liveReading(data, problems)
  return (
    <div className="nw-ol nw-olsum">
      <LiveHero tone={reading.tone} resolved={data.today.issues_resolved} />
      <BandStrip tone={reading.tone} />
      <ConditionTile data={data} reading={reading} onOpenHosts={onViewHosts ?? onOpen} />
      <button type="button" className="olsum-open" onClick={onOpen}>
        Open Outlook →
      </button>
    </div>
  )
}

function Loaded({ data, history, problems, disks, insights, dismissedInsightIds, onDismissInsight, onRestoreInsight, insightRatings, onRateInsight, onUnrateInsight, onRunInsights, onGraphFeedback, onLabelSituation, insightAskEnabled, onAsk, onOpenHosts, onViewOverdue, onOpenDisks, onOpenDay }: {
  data: ConfidenceData
  history: ConfidenceDay[]
  problems: ActiveProblem[]
  disks?: FillingDisk[] | null
  insights?: EnvironmentInsight[] | null
  dismissedInsightIds?: string[] | null
  onDismissInsight?: (identity: string) => void
  onRestoreInsight?: (identity: string) => void
  insightRatings?: Record<string, InsightRatingState> | null
  onRateInsight?: (identity: string, thumb: 'up' | 'down', reason?: string) => void
  onUnrateInsight?: (identity: string) => void
  onRunInsights?: (lane: 'fast' | 'slow') => Promise<unknown>
  onGraphFeedback?: (edgeId: string, action: 'confirm' | 'reject') => Promise<unknown>
  onLabelSituation?: (situationId: string, label: string, correctedRoot?: string) => Promise<unknown>
  insightAskEnabled?: boolean
  onAsk: (text: string) => void
  onOpenHosts: () => void
  onViewOverdue?: () => void
  onOpenDisks: () => void
  onOpenDay: (slot: DaySlot) => void
}) {
  // The whole live reading — still-open count, host severity split, hero tone.
  // Prefers the live problems list (it polls faster than the cached confidence
  // payload, so the footer, pill and today cell never disagree) and falls back
  // to the payload; see `liveReading`.
  const reading = liveReading(data, problems)
  const { active, tone } = reading
  const affected = data.affected_hosts
  const needsLook = problems.filter((p) => p.severity >= HIGH_SEVERITY).length
  // Problems that opened today (UTC, matching the backend's rollup day) and
  // are still unresolved — today's cell on the 7-day strip.
  // "Today" starts at the BROWSER'S local midnight — matches the backend's
  // NETWATCH_DAY_TZ day windows (operators view their own environment, so the
  // browser timezone and the configured day timezone agree in practice).
  const dayStart = new Date().setHours(0, 0, 0, 0) / 1000
  const todayProblems = problems.filter((p) => p.since >= dayStart)
  const liveNewUnresolved = todayProblems.length
  // Worst severity of TODAY'S problems (not all active) for the today cell.
  const liveTodayWorst = todayProblems.reduce((m, p) => Math.max(m, p.severity), -1)
  const slots = buildWeek(history, data, liveTodayWorst, liveNewUnresolved)
  // Footer "overdue" pill: still-open problems that opened on earlier days
  // (carried over into today). Together with today's new-unresolved these sum
  // to the footer's total still-open count. Dismiss lasts until remount.
  // The pill's icon + colour follow the WORST severity among the overdue
  // problems, same registry ramp as everything else.
  const overdueProblems = problems.filter((p) => p.since > 0 && p.since < dayStart)
  const carriedOver = overdueProblems.length
  const worstOverdue = overdueProblems.reduce((m, p) => Math.max(m, p.severity), 0)
  const overdueCls = worstOverdue >= 5 ? ' sv5' : worstOverdue === 4 ? ' sv4' : worstOverdue >= 2 ? '' : ' svlow'
  const [overdueDismissed, setOverdueDismissed] = useState(false)

  // Prefer the backend's AI one-liner; fall back to a deterministic sentence
  // (pre-warm / degraded payloads carry no insight).
  const fallbackInsight =
    active === 0
      ? `All ${data.hosts} monitored host${data.hosts === 1 ? '' : 's'} clear — no active problems.`
      : `${affected} host${affected === 1 ? '' : 's'} affected across ${active} active problem${active === 1 ? '' : 's'}` +
        `${needsLook > 0 ? `, ${needsLook} high-severity` : ''}.`
  const insight = (data.insight && data.insight.trim()) || fallbackInsight
  const insightAsk =
    active > 0
      ? 'Give me a correlation analysis of the current active problems'
      : 'Give me an overview of the overall health right now.'
  // Per-user dismissals (SPA only): dismissed lines leave the tile rotation
  // and live in the drawer's "Dismissed" section until restored / revived.
  const dismissedSet = new Set(dismissedInsightIds ?? [])
  const activeInsights = (insights ?? []).filter(
    (i) => !i.identity || !dismissedSet.has(i.identity),
  )
  const dismissedInsights = (insights ?? []).filter(
    (i) => i.identity && dismissedSet.has(i.identity),
  )

  return (
    <>
      <LiveHero tone={tone} resolved={data.today.issues_resolved} />

      <BandStrip tone={tone} />

      <EnvHealth score={data.score} onAsk={onAsk} />

      <div className="tiles">
        <ConditionTile data={data} reading={reading} onOpenHosts={onOpenHosts} />

        {/* Capacity trend — live feed from the 3h disk-fill forecast sweep */}
        <DriveFillTile disks={disks} onOpenDisks={onOpenDisks} />
      </div>

      <InsightTile
        items={activeInsights}
        dismissed={dismissedInsights}
        fallbackText={insight}
        fallbackAsk={insightAsk}
        onAsk={onAsk}
        askEnabled={insightAskEnabled}
        onDismiss={onDismissInsight}
        onRestore={onRestoreInsight}
        ratings={insightRatings}
        onRate={onRateInsight}
        onUnrate={onUnrateInsight}
        onRun={onRunInsights}
        onGraphFeedback={onGraphFeedback}
        onLabelSituation={onLabelSituation}
      />

      <div className="week2">
        <div className="wt">
          <span>7-day outlook</span>
          <span style={{ color: 'var(--faint)' }}>
            {slots[0].monthDay} – {slots[slots.length - 1].monthDay} · incidents still open
          </span>
        </div>
        <div className="days2">
          {slots.map((s) => {
            const has = s.newUnresolved != null
            const glyph = has ? dayGlyph(s) : null
            // Problems that opened that day and never resolved (live for today).
            const stillOpen = s.newUnresolved
            // Box border + shading follow the worst still-active severity:
            // Disaster red, High salmon, everything lower the amber default.
            const warn = glyph !== null && glyph.level !== 'ok'
            const sevCls = warn && typeof glyph.level === 'number' ? ` s${glyph.level}` : ''
            // Count colour tracks the worst host severity exactly like the
            // icon: Disaster deep red, High salmon, Warning/Average amber,
            // Info/Unclassified soft; plain ink on a clean day.
            const sevLevel = glyph && typeof glyph.level === 'number' ? glyph.level : null
            const numCls = !stillOpen || sevLevel == null ? ''
              : sevLevel >= 5 ? ' c-sev5'
                : sevLevel === 4 ? ' c-sev4'
                  : sevLevel >= 2 ? ' amber'
                    : ' c-soft'
            return (
              <div
                className={`d2${warn ? ` sel${sevCls}` : ''}${has ? '' : ' empty'}`}
                key={s.key}
                onClick={has ? () => onOpenDay(s) : undefined}
                role={has ? 'button' : undefined}
                tabIndex={has ? 0 : undefined}
              >
                <span className="dw">{s.weekday}</span>
                {glyph
                  ? <SeverityIcon level={glyph.level} size={22} title={glyph.label} />
                  : <span className="d2emo">·</span>}
                <span className={`dn${numCls}`}>{stillOpen ?? '–'}</span>
              </div>
            )
          })}
        </div>
        <div className="wfoot">
          <b>{active} incident{active === 1 ? '' : 's'} still open</b>
          {carriedOver > 0 && !overdueDismissed && (
            <span
              className={`wover${overdueCls}`}
              title={`Still-open problems that opened on earlier days — worst: ${SEV_LABELS[worstOverdue] ?? `severity ${worstOverdue}`}`}
            >
              <SeverityIcon level={worstOverdue} size={12} />
              {/* Hosts navigate to the overdue-filtered incidents view; the
                  widget (no router) falls back to a chat investigation. */}
              <button
                className="wover-link"
                onClick={onViewOverdue ?? (() => onAsk(`Show me the ${carriedOver} overdue problems — the ones opened on earlier days that are still unresolved — worst severity first.`))}
              >
                {carriedOver} overdue
              </button>
              <button className="wover-x" aria-label="Dismiss" onClick={() => setOverdueDismissed(true)}>×</button>
            </span>
          )}
        </div>
      </div>
    </>
  )
}
